/**
 * Portal Translations — Katcon, Nexteer, and shared portal strings.
 * Imported by GlobalTranslationProvider into the single t() lookup chain.
 *
 * Key convention:
 *   portal.*          — shared across portals
 *   katcon.*          — Katcon-specific
 *   nexteer.*         — Nexteer-specific
 */

export const portalTranslations: Record<"en" | "es", Record<string, string>> = {
  en: {
    // =========================================================================
    // PORTAL SHARED
    // =========================================================================
    "portal.demo_banner_prefix": "Demo Mode",
    "portal.reset_demo": "Reset Demo",
    "portal.supplier_compliance_portal": "Supplier Compliance Portal",
    "portal.mv_deadline": "MV Deadline: June 1",
    "portal.days": "days",
    "portal.days_remaining": "days remaining",

    // Common tab labels
    "portal.tab_overview": "Overview",
    "portal.tab_suppliers": "Suppliers",
    "portal.tab_alerts": "Alerts",
    "portal.tab_option1": "Option 1: Compliance",
    "portal.tab_option2": "Option 2: Early Payment",
    "portal.tab_digital_passport": "Digital Passport",

    // Common table headers
    "portal.th_supplier": "Supplier",
    "portal.th_status": "Status",
    "portal.th_risk": "Risk",
    "portal.th_component": "Component",
    "portal.th_material": "Material",
    "portal.th_rfc": "RFC",
    "portal.th_hs_code": "HS Code",
    "portal.th_mv_status": "MV Status",
    "portal.th_question": "Question",
    "portal.th_answer": "Answer",
    "portal.th_monthly_volume": "Monthly Volume",
    "portal.th_connection": "Connection",
    "portal.th_compliance": "Compliance",
    "portal.th_terms": "Terms",

    // Common statuses
    "portal.status_clean": "Clean",
    "portal.status_flagged": "Flagged",
    "portal.status_pending": "Pending",
    "portal.status_verified": "Verified",
    "portal.status_missing": "Missing",
    "portal.status_blocked": "BLOCKED",
    "portal.status_active": "ACTIVE",
    "portal.status_eligible": "ELIGIBLE",
    "portal.status_not_eligible": "NOT ELIGIBLE",
    "portal.status_exempt": "EXEMPT",
    "portal.status_match": "Match",
    "portal.status_mismatch": "Mismatch",
    "portal.status_on_file": "On file",
    "portal.status_ready": "Ready",
    "portal.status_pass": "PASS",
    "portal.status_fail": "FAIL",
    "portal.status_updated": "Updated",
    "portal.status_updating": "Updating...",

    // Common risk levels
    "portal.risk_low": "Low",
    "portal.risk_medium": "Medium",
    "portal.risk_high": "High",
    "portal.risk_critical": "Critical",
    "portal.risk_none": "None",

    // Common labels
    "portal.efos_label": "EFOS:",
    "portal.cfdi_label": "CFDI:",
    "portal.mill_cert_label": "Mill Cert:",
    "portal.steel_label": "Steel:",
    "portal.mv_label": "MV:",
    "portal.rfc_label": "RFC:",
    "portal.unknown": "Unknown",
    "portal.yes": "Yes",
    "portal.no": "No",
    "portal.none": "None",

    // Common buttons
    "portal.download_results": "Download Results",
    "portal.export_pdf": "Export PDF",
    "portal.try_another": "Try another",
    "portal.close_return": "Close & Return to Portal",

    // RFC Screening (shared between Katcon and Nexteer)
    "portal.rfc_screening_title": "Try It: Screen Your Suppliers",
    "portal.rfc_textarea_placeholder": "Enter RFCs — one per line or comma-separated",
    "portal.rfc_screening_progress": "Screening RFC",
    "portal.run_screening": "Run Screening",
    "portal.clean_demo": "Clean (demo mode)",

    // Option 1 shared
    "portal.opt1_title": "Option 1: Compliance Verification",
    "portal.opt1_step1_title": "Supplier sends docs",
    "portal.opt1_step2_title": "Alisio parses & verifies",
    "portal.opt1_step2_detail": "Transaction values cross-referenced vs SAT. RFC screened vs 69-B. HS codes validated. Steel pricing checked vs LME.",
    "portal.opt1_step3_title": "Certificate generated",
    "portal.opt1_step4_title": "Supplier signs",
    "portal.opt1_step4_detail": "Digital signature = formal certification. Documented due diligence record created. Document is timestamped and MV-ready.",
    "portal.opt1_what_catches": "What Option 1 Catches",
    "portal.opt1_see_it_work": "See it work",

    // Liability language (corrected — obligado solidario)
    "portal.liability_today_title": "TODAY: Joint and several liability, unverified data",
    "portal.liability_today_detail": "Sub-supplier data goes into pedimento and MV without formal certification. Under the 2026 reform, both importer and broker share joint and several liability (obligado solidario) for every value declared. The previous broker exemption for client-provided inaccurate data is gone. No documented due diligence trail. Audit response requires reconstructing documentation from scattered sources.",
    "portal.liability_opt1_title": "WITH OPTION 1: Documented due diligence per transaction",
    "portal.liability_opt1_detail": "Every transaction has a signed compliance certificate. Supplier formally certifies their values and origin. Verified against SAT before filing. Audit response is immediate — pull the Digital Compliance Jacket. Creates the documented verification record that the 2026 reform requires IMMEX companies to maintain, and provides a contractual basis for holding suppliers accountable.",

    // Option 2 shared
    "portal.opt2_title": "Option 2: Early Payment Program",
    "portal.opt2_detail": "Builds on Option 1. Sub-suppliers who want faster cash voluntarily connect their systems and get pre-qualified. Verified compliance data unlocks early payment. The incentive is money, not a mandate.",
    "portal.opt2_opted_in": "Suppliers Opted In",
    "portal.opt2_acceleration": "Payment Acceleration",
    "portal.opt2_prerequisite": "Prerequisite: compliance must pass",
    "portal.opt2_table_title": "Supplier Early Payment Status",
    "portal.opt2_failed": "Failed",
    "portal.opt2_erp_connected": "ERP Connected",
    "portal.opt2_email_only": "Email Only",
    "portal.opt2_step1_title": "Pass compliance",
    "portal.opt2_step1_detail": "Already in Option 1, sending docs, signing certs.",
    "portal.opt2_step2_title": "Supplier opts in",
    "portal.opt2_step2_detail": "Voluntarily connects ERP/shares CIEC. THIS is where ERP access is relevant.",
    "portal.opt2_step3_title": "Early payment activated",
    "portal.opt2_step3_detail": "NET 15 instead of NET 60. Funded through Alisio's financing layer.",

    // ERP/SAP Integration shared
    "portal.erp_recommended": "RECOMMENDED STARTING POINT",
    "portal.erp_phase2": "PHASE 2 — OPTIONAL",
    "portal.erp_case2_title": "Case 2: Verify Your Suppliers",
    "portal.erp_case2_data_header": "Data read from ERP:",
    "portal.erp_case2_data_1": "Supplier master data (RFC, name, supplier ID)",
    "portal.erp_case2_data_2": "Purchase order and goods receipt references",
    "portal.erp_case2_data_3": "Incoming CFDI UUIDs (folio fiscal)",
    "portal.erp_data_objects": "Data Objects",
    "portal.erp_data_objects_value": "Supplier Master, Purchasing, AP (read)",
    "portal.erp_integration": "Integration",
    "portal.erp_auth": "Auth",
    "portal.erp_auth_value": "Certificate or OAuth",
    "portal.erp_setup": "Setup",
    "portal.erp_setup_1_2_days": "1-2 days",
    "portal.erp_case1_data_header": "Additional data beyond Case 2:",
    "portal.erp_case1_data_1": "Issued CFDIs",
    "portal.erp_case1_data_2": "Cost accounting data (for RVC Net Cost)",
    "portal.erp_case1_data_3": "BOM structures",
    "portal.erp_case1_data_4": "Own RFC/tax data",
    "portal.erp_additional_data": "Additional Data",
    "portal.erp_additional_data_value": "Financial Docs, BOM, Cost (read)",
    "portal.erp_setup_2_3_days": "2-3 days (broader scope)",
    "portal.erp_not_required_title": "What Alisio Does NOT Require",
    "portal.erp_not_required_1": "No custom code or extensions",
    "portal.erp_not_required_2": "No changes to existing user roles or permissions",
    "portal.erp_not_required_3": "No write access to any data object",
    "portal.erp_not_required_4": "No modifications to existing workflows or screens",
    "portal.erp_not_required_5": "No development environment access",
    "portal.erp_arch_title": "Architecture",
    "portal.erp_arch_read_only": "Read-only API",
    "portal.erp_arch_alisio": "Alisio Verification",
    "portal.erp_arch_alisio_sub": "Parse, validate, calculate",
    "portal.erp_arch_sat": "SAT / SW SmartWeb",
    "portal.erp_arch_sat_sub": "Cross-reference",
    "portal.erp_arch_dashboard": "Compliance Dashboard",
    "portal.erp_arch_footer": "The heavy lifting happens Alisio-to-SAT, not Alisio-to-ERP.",
    "portal.erp_quick_ref": "Quick Reference",
    "portal.erp_qa_custom_code": "Custom code?",
    "portal.erp_qa_write_access": "Write access?",
    "portal.erp_qa_auth": "Auth method",
    "portal.erp_qa_impact": "Impact on users",

    // Email processing demo shared
    "portal.email_demo_title": "Try It: Process a Supplier Email",
    "portal.email_demo_detail": "Select a sample email below and watch Alisio parse, verify, and generate a compliance certificate in real time.",
    "portal.pipeline_receiving": "Receiving",
    "portal.pipeline_parsing": "Parsing",
    "portal.pipeline_verifying": "Verifying",
    "portal.pipeline_certificate": "Certificate",
    "portal.phase_receiving": "Receiving email...",
    "portal.phase_extracting": "Extracting fields...",
    "portal.phase_verifying": "Verifying against external sources...",
    "portal.phase_complete": "Verification complete",
    "portal.cert_title": "Compliance Certificate — Ready for Signature",
    "portal.cert_all_verified": "ALL VERIFIED",
    "portal.cert_supplier": "Supplier:",
    "portal.cert_id": "Certificate ID:",
    "portal.cert_fields_verified": "Fields verified:",
    "portal.cert_generated": "Generated:",
    "portal.cert_signature_line": "Digital signature line",
    "portal.cert_signature_detail": "Supplier signs here — documented due diligence record created — document is timestamped and MV-ready",
    "portal.cert_issues_flagged": "issue(s) flagged",
    "portal.cert_processing_time": "Processing time:",

    // Common field labels
    "portal.field_rfc_emisor": "RFC Emisor",
    "portal.field_cfdi_uuid": "CFDI UUID",
    "portal.field_invoice_total": "Invoice Total",
    "portal.field_hs_declared": "HS Code Declared",
    "portal.field_steel_supplier": "Steel Supplier",
    "portal.field_heat_lot": "Heat Lot",
    "portal.field_melted_poured": "Melted & Poured",
    "portal.field_material_grade": "Material Grade",
    "portal.field_lme_price": "Steel Price vs LME",

    // Verification methods
    "portal.verify_efos": "EFOS 69-B via SW SmartWeb",
    "portal.verify_sat_soap": "SAT SOAP endpoint",
    "portal.verify_alisio_hs": "Alisio HS Validation Engine",
    "portal.verify_supplier_master": "Supplier master cross-reference",
    "portal.verify_mill_cert_db": "Mill cert database",
    "portal.verify_usmca_origin": "USMCA origin determination",
    "portal.verify_spec_match": "Spec match vs PO requirements",
    "portal.verify_lme_benchmark": "LME benchmark comparison",

    // Verification results
    "portal.result_clean_efos": "Clean — not on 69-B list",
    "portal.result_vigente": "Vigente — valid and active",
    "portal.result_supplier_verified": "Verified — known USMCA-originating mill",
    "portal.result_usmca_origin": "USMCA originating — melted and poured in Mexico",
    "portal.result_spec_match": "Match — within specification",
    "portal.result_lme_normal": "Within range — normal",
    "portal.result_heat_lot_valid": "Valid — matches records",

    // Scope statements shared
    "portal.what_alisio_does": "What Alisio Does",
    "portal.what_alisio_does_not": "What Alisio Does NOT Do",

    // MV shared
    "portal.mv_banner_title": "Manifestación de Valor — Electronic Filing Mandatory June 1, 2026",
    "portal.mv_shared_liability": "Joint and several liability (obligado solidario)",
    "portal.mv_fines": "Fines: up to MXN $53,500 per operation",
    "portal.mv_false_declarations": "False declarations: 70-250% of goods value",

    // =========================================================================
    // KATCON SPECIFIC
    // =========================================================================
    "katcon.demo_banner": "Demo Mode — Showing representative data for Katcon's supply chain. Connect your SAP for live supplier data.",
    "katcon.tab_customs": "Customs & 232",
    "katcon.tab_sap": "SAP Integration",

    // Overview tab
    "katcon.rfc_help": "Enter supplier RFCs to see EFOS 69-B screening results. Try: FIC780901EEE (flagged) or FMB860101AAA (clean).",
    "katcon.stat_hs_issues": "HS Code Issues",
    "katcon.stat_hs_issues_sub": "of {count} suppliers",
    "katcon.stat_mv_ready": "MV Ready",
    "katcon.stat_232_exposure": "Section 232 Exposure",
    "katcon.stat_232_at_risk": "MXN — duties at risk",
    "katcon.stat_232_clear": "All clear",
    "katcon.stat_rvc_fmb": "RVC (FMB)",
    "katcon.stat_rvc_threshold": "Threshold: 65%",
    "katcon.overview_title": "Supplier Compliance Overview",
    "katcon.th_hs_status": "HS Status",
    "katcon.th_232_exposure": "232 Exposure",
    "katcon.correction_history": "Correction History",

    // Suppliers tab
    "katcon.hs_classification": "HS Classification",
    "katcon.section_232": "Section 232",
    "katcon.apply_correction": "Apply Correction",
    "katcon.rvc_compliance": "RVC Compliance",
    "katcon.duty_50_pct": "50% duty on steel content",
    "katcon.exempt_via_correction": "232 exemption secured via HS correction",
    "katcon.no_232_exposure": "No 232 exposure",

    // Customs tab
    "katcon.customs_232_title": "Section 232 Steel & Aluminum Exposure",
    "katcon.total_steel_content": "Total steel content (MXN)",
    "katcon.verified_usmca": "Verified USMCA origin",
    "katcon.unverified_suppliers": "Unverified suppliers",
    "katcon.est_232_exposure": "Est. 232 exposure (50%)",
    "katcon.th_origin": "Origin",
    "katcon.th_with_alisio": "With Alisio",
    "katcon.tubes_issue_title": "The tubes issue",
    "katcon.tubes_issue_detail": "is verified origin but wrong HS code. If classified as 7306.40, Section 232 applies to the steel content. Reclassified as 8708.92, it's a USMCA-qualifying auto part — 232 exempt. This single reclassification saves approximately MXN $287,400 per shipment.",
    "katcon.tubes_corrected_suffix": "— Correction applied.",
    "katcon.cbp_title": "Strengthening the CBP Classification Argument",
    "katcon.cbp_intro": "Katcon has already attempted to get CBP to accept reclassification from 7306.40 to 8708.92 and encountered resistance. This is an evidence problem — CBP requires principal use documentation, manufacturing specifications, and BOM traceability. Alisio builds this evidentiary package automatically.",
    "katcon.evidence_bom": "BOM Traceability",
    "katcon.evidence_bom_detail": "Each tube has a specific part number tied to a specific exhaust assembly for an identified OEM program (e.g., GM Silverado Euro 7 SCR). This is principal use evidence — the tube was manufactured to specification, not as a generic product.",
    "katcon.evidence_bom_source": "Katcon SAP — BOM data",
    "katcon.evidence_mfg": "Manufacturing Specifications",
    "katcon.evidence_mfg_detail": "Mill certs and POs show the tube was ordered to exhaust-system specifications (specific alloy, wall thickness, diameter, bend radius) unsuitable for general tube applications.",
    "katcon.evidence_mfg_source": "Tell Tale — parsed mill certificates + SAP PO data",
    "katcon.evidence_enduse": "End-Use Declaration",
    "katcon.evidence_enduse_detail": "The compliance certificate signed by Tubos y Perfiles includes a declaration of intended use — manufactured specifically for automotive exhaust applications.",
    "katcon.evidence_enduse_source": "Option 1 compliance certificate",
    "katcon.evidence_precedent": "Precedent Research",
    "katcon.evidence_precedent_detail": "Cross-reference against CBP's CROSS database (220,000+ rulings) for similar cases where exhaust components were successfully classified under 8708 despite being made from steel tubing.",
    "katcon.evidence_precedent_source": "Alisio HS validation engine",
    "katcon.evidence_assembled": "Evidence package assembled",
    "katcon.correction_propagation": "When the reclassification is accepted — through a binding ruling, protest, or negotiated resolution — the correction propagates through all 6 downstream documents automatically. One change in the portal, six documents updated.",
    "katcon.mill_cert_monitoring": "Mill Certificate Change Monitoring",
    "katcon.mill_cert_intro": "Mill certificates are not static. Suppliers change steel sources between batches, and each change can affect the classification argument, origin determination, and USMCA qualification.",
    "katcon.monitor_source_change": "Supplier Source Change",
    "katcon.monitor_source_detail": "Detects when a mill cert for the same part number shows a different supplier, alloy, or melt-and-pour location. A change in steel origin can invalidate USMCA qualification and reintroduce 232 exposure.",
    "katcon.monitor_spec_drift": "Specification Drift",
    "katcon.monitor_spec_detail": "Flags when material grade or specs change on a mill cert (e.g., SAE 304L to a different grade). Could undermine the 'manufactured to exhaust specification' argument for the CBP dispute.",
    "katcon.monitor_price_anomaly": "Price Anomaly",
    "katcon.monitor_price_detail": "When steel pricing drops significantly below LME benchmark, may indicate transshipped material with false origin claims. Flags certificates that may not reflect actual source.",
    "katcon.monitor_missing": "Missing or Expired",
    "katcon.monitor_missing_detail": "Flags when no mill cert is provided for a shipment, or when a certificate is older than the shipment date. Filing a USMCA origin claim without current documentation creates audit exposure.",
    "katcon.immex_title": "IMMEX Program Health",
    "katcon.immex_status": "IMMEX Program Status",
    "katcon.immex_iva_cert": "IVA/IEPS Certification",
    "katcon.immex_efos": "Supplier EFOS Screening",
    "katcon.immex_efos_value": "1 supplier flagged — exposure",
    "katcon.immex_subsupplier": "Sub-supplier Verification",
    "katcon.immex_subsupplier_value": "5/8 fully verified",
    "katcon.immex_annex24": "Annex 24 Compliance",
    "katcon.immex_annex30": "Annex 30 Compliance",
    "katcon.not_managed": "Not managed by Alisio",
    "katcon.risk_factors_title": "Risk Factors",
    "katcon.risk_factor_efos": "Fundición Industrial Celaya on EFOS presumido — any CFDI in IMMEX docs creates audit risk",
    "katcon.risk_factor_certs": "3 suppliers without signed compliance certificates",
    "katcon.risk_factor_hs": "HS code misclassification(s) in active supply chain",
    "katcon.article_25_title": "Article 2.5 Clawback",
    "katcon.article_25_detail": "When finished goods export under USMCA, IMMEX duty exemptions on non-FTA inputs get clawed back at Mexico's IGI rate — which increased 10-50% in January 2026. Every input Alisio verifies as USMCA-originating reduces this exposure.",
    "katcon.scope_1": "Continuous EFOS 69-B screening",
    "katcon.scope_2": "Real-time CFDI verification",
    "katcon.scope_3": "HS code validation and misclassification detection",
    "katcon.scope_4": "Mill certificate parsing for melt-and-pour origin",
    "katcon.scope_5": "RVC calculation (Net Cost + Transaction Value)",
    "katcon.scope_6": "Compliance certificates with digital signature",
    "katcon.scope_7": "Timestamped audit trail",
    "katcon.scope_8": "MV-ready documentation packages",
    "katcon.scope_not_1": "Annex 24/30 inventory control (stays in SAP)",
    "katcon.scope_not_2": "Pedimento filing in VUCEM (your broker handles this)",
    "katcon.scope_not_3": "Replace your customs broker",
    "katcon.scope_not_4": "IMMEX program management",
    "katcon.scope_not_5": "Physical cargo inspection",
    "katcon.scope_footer": "Alisio verifies the data before it reaches your customs systems. Your broker files with confidence because the underlying data has been validated.",

    // Katcon Option 1
    "katcon.opt1_step1_detail": "Mill certificates, invoices, origin declarations sent to verificacion@katcon.com. Same documents they already produce, different address.",
    "katcon.opt1_step3_detail": "Pre-filled compliance certificate with verified data. Issues flagged for Katcon review before sending to supplier.",
    "katcon.opt1_catch_efos_title": "EFOS — Fundición Celaya",
    "katcon.opt1_catch_efos_detail": "On SAT's 69-B presumido list. Option 1 catches this the day the supplier is listed — before their next invoice hits a pedimento.",
    "katcon.opt1_catch_cfdi_title": "CFDI Cancelled — Metalúrgica del Norte",
    "katcon.opt1_catch_cfdi_detail": "Invoice MNO-2026-0847 ($234,600 MXN) cancelled after Katcon booked it. Real-time monitoring catches cancellation within hours.",
    "katcon.opt1_catch_tubes_title": "Tubes — Tubos y Perfiles MTY",
    "katcon.opt1_catch_tubes_resolved": "Resolved — Reclassified from 7306.40 to 8708.92. Evidence package assembled. Correction propagated across 6 documents.",
    "katcon.opt1_catch_tubes_unresolved": "Classified as 7306.40 (steel tubes) instead of 8708.92 (exhaust parts). Katcon has attempted reclassification with CBP and encountered resistance. Option 1 builds the evidentiary package CBP requires: BOM traceability showing principal use, manufacturing specifications from mill certs, end-use declaration in the compliance certificate, and precedent from CROSS database.",
    "katcon.opt1_catch_mill_title": "Mill certs missing — 3 suppliers",
    "katcon.opt1_catch_mill_detail": "Without melt-and-pour documentation, cannot claim USMCA origin. At 50% Section 232, each unverified shipment is direct cost exposure.",
    "katcon.opt1_sap_note_title": "SAP S/4HANA Integration:",
    "katcon.opt1_sap_note_detail": "Read-only Communication Arrangement on supplier master and purchasing catalogs. 1-2 days of configuration. See the SAP Integration tab for full technical details.",

    // Katcon Option 2 (supplement to shared)
    "katcon.opt2_key_message": "This is what we showed in the demo on Friday. It's the right product for suppliers who want the benefit. For compliance verification, Option 1 is all Katcon needs. Option 2 is the incentive that motivates some suppliers to go deeper voluntarily.",

    // Katcon SAP
    "katcon.sap_title": "SAP S/4HANA Public Cloud Integration",
    "katcon.sap_detail": "Read-only. No custom code. Fits your Fit to Standard approach.",
    "katcon.sap_case2_detail": "Alisio verifies Katcon's sub-suppliers using data already in Katcon's SAP. Sub-suppliers don't grant any access.",
    "katcon.sap_catalogs": "Business Catalogs",
    "katcon.sap_catalogs_value": "Supplier Master, Purchasing, AP (read)",
    "katcon.sap_integration_value": "Communication Arrangement",
    "katcon.sap_not_1": "No custom ABAP code",
    "katcon.sap_not_2": "No changes to existing Business Roles",
    "katcon.sap_not_3": "No write access to any catalog",
    "katcon.sap_not_4": "No Fiori app modifications",
    "katcon.sap_not_5": "No ABAP development environment access",
    "katcon.sap_case1_title": "Case 1: Verify Katcon's Own Compliance",
    "katcon.sap_case1_detail": "Katcon verifies its own CFDI issuance, tax standing, and USMCA documentation for OEM customers.",
    "katcon.sap_case1_1": "Katcon's issued CFDIs",
    "katcon.sap_arch_sap": "Katcon SAP",
    "katcon.sap_arch_sap_sub": "S/4HANA Public Cloud",
    "katcon.sap_arch_comm": "Communication Arrangement",
    "katcon.sap_arch_portal": "/katcon-portal",
    "katcon.sap_arch_footer": "The heavy lifting happens Alisio-to-SAT, not Alisio-to-SAP.",
    "katcon.sap_qa_fit": "Fit to Standard",

    // EDICOM complement
    "katcon.edicom_title": "Complemento con EDICOM",
    "katcon.edicom_detail": "EDICOM maneja el timbrado de CFDI (lado de emisión). Alisio maneja la verificación (lado de cumplimiento). Nunca competimos — siempre somos complementarios. Los datos de EDICOM pueden proporcionar la ruta existente de SAP para CFDIs entrantes.",
    "katcon.sap_edicom_note": "EDICOM may provide an existing data path for incoming CFDIs via SAP integration.",

    // Alerts (Katcon)
    "katcon.alert_resolved_title": "Resolved — HS Misclassification (Tubos y Perfiles MTY)",
    "katcon.alert_resolved_detail": "HS 7306.40 → 8708.92 — Section 232 exemption secured, 6 documents updated",

    // Correction workflow
    "katcon.correction_title": "HS Code Correction — Live Propagation",
    "katcon.correction_detail": "One correction cascades through the entire document chain. Like updating a calendar invite — every linked document gets the change.",
    "katcon.correction_section": "Correction",
    "katcon.correction_field": "Field",
    "katcon.correction_current": "Current (incorrect)",
    "katcon.correction_current_value": "7306.40 — Welded Steel Tubes",
    "katcon.correction_corrected": "Corrected",
    "katcon.correction_corrected_value": "8708.92 — Exhaust System Parts",
    "katcon.correction_applied_by": "Applied by",
    "katcon.correction_applied_by_value": "Alisio — HS Validation Engine",
    "katcon.correction_timestamp": "Timestamp",
    "katcon.correction_downstream": "Downstream documents",
    "katcon.correction_downstream_count": "6 affected",
    "katcon.confirm_correction": "Confirm Correction",
    "katcon.propagating": "Propagating...",
    "katcon.correction_applied": "Correction Applied",
    "katcon.downstream_docs": "Downstream Documents",
    "katcon.doc_invoice": "Commercial Invoice",
    "katcon.doc_cove": "COVE (Comprobante de Valor Electrónico)",
    "katcon.doc_mv": "Manifestación de Valor (E2)",
    "katcon.doc_usmca": "USMCA Certificate of Origin",
    "katcon.doc_232": "Section 232 Determination",
    "katcon.doc_pedimento": "Pedimento",
    "katcon.doc_232_exempt": "232 EXEMPT",
    "katcon.correction_summary": "1 correction → 6 documents updated → USMCA qualification confirmed → Section 232 exemption secured",
    "katcon.propagation_time": "Time to propagate:",
    "katcon.manual_time": "Manual process: est. 4-6 hours",

    // Tariff calculator
    "katcon.roi_title": "Compliance ROI — Duties Avoided",
    "katcon.roi_monthly_shipments": "Monthly shipments verified",
    "katcon.roi_duties_without": "Duties without verification",
    "katcon.roi_duties_with": "Duties with Alisio",
    "katcon.roi_monthly_savings": "Monthly savings",
    "katcon.roi_without": "Without verification",
    "katcon.roi_with": "With Alisio",
    "katcon.roi_ytd": "YTD savings",
    "katcon.roi_annualized": "Annualized",
    "katcon.roi_rate_without": "Effective rate without",
    "katcon.roi_rate_with": "Effective rate with Alisio",
    "katcon.roi_disclaimer": "Calculated from representative data. Connect your SAP for calculations based on actual purchase volumes.",
    "katcon.tariff_title": "Tariff Offset Calculator",
    "katcon.tariff_help": "See the financial impact of each verification step per shipment.",
    "katcon.tariff_hs_code": "HS Code",
    "katcon.tariff_shipment_value": "Shipment Value (MXN)",
    "katcon.tariff_steel_content": "Steel/Aluminum Content (MXN)",
    "katcon.tariff_calculate": "Calculate",
    "katcon.tariff_scenario": "Scenario",
    "katcon.tariff_igi_rate": "IGI Rate",
    "katcon.tariff_igi_amount": "IGI Amount",
    "katcon.tariff_232_rate": "232 Rate",
    "katcon.tariff_232_amount": "232 Amount",
    "katcon.tariff_total": "Total Duties",
    "katcon.tariff_savings": "Savings",

    // Landed cost modeler
    "katcon.landed_title": "Landed Cost Intelligence",
    "katcon.landed_help": "True landed cost per product — calculated from SAP purchase data + verified tariff treatment.",
    "katcon.landed_sap": "SAP S/4HANA",
    "katcon.landed_sap_sub": "Price, freight, BOM",
    "katcon.landed_alisio": "Alisio Rate Engine",
    "katcon.landed_alisio_sub": "IGI, PROSEC, 232",
    "katcon.landed_output": "Landed Cost",
    "katcon.landed_output_sub": "Per unit, per shipment",
    "katcon.landed_price": "Price/Unit (MXN)",
    "katcon.landed_qty": "Qty",
    "katcon.landed_freight": "Freight (MXN)",
    "katcon.landed_steel": "Steel Content (MXN)",
    "katcon.landed_origin_verified": "Origin Verified",
    "katcon.landed_prosec": "PROSEC",
    "katcon.landed_line_purchase": "Purchase Price",
    "katcon.landed_line_freight": "Freight + Insurance",
    "katcon.landed_line_igi": "Import Duty (IGI)",
    "katcon.landed_line_prosec": "PROSEC Adjustment",
    "katcon.landed_line_usmca": "USMCA Preferential",
    "katcon.landed_line_232": "Section 232",
    "katcon.landed_line_iva": "IVA (16%)",
    "katcon.landed_iva_detail": "Credited with IMMEX cert",
    "katcon.landed_line_dta": "DTA",
    "katcon.landed_dta_detail": "0.8% capped",
    "katcon.landed_line_broker": "Customs Broker Fee (est.)",
    "katcon.landed_line_total": "TOTAL LANDED COST",

    // Email demo katcon-specific
    "katcon.email_hs_issue": "HS Issue",
    "katcon.email_corrected": "Corrected",
    "katcon.email_hs_mismatch": "MISMATCH — should be 8708.92 (exhaust parts)",
    "katcon.email_po_match": "Match — within 0.2% of PO value",
    "katcon.cert_hs_warn": "HS Code 7306.40 should be 8708.92 — recommend correction before certificate issuance.",
    "katcon.cert_hs_corrected": "All fields verified. HS code corrected to 8708.92 — certificate ready for immediate issuance.",
    "katcon.cert_warn_review": "field(s) flagged for Katcon review before sending to supplier.",

    // =========================================================================
    // NEXTEER SPECIFIC
    // =========================================================================
    "nexteer.demo_banner": "Demo Mode — Showing representative data for Nexteer's QRO supply chain. Connect your ERP for live supplier data.",
    "nexteer.icts_deadline": "ICTS MY 2027",
    "nexteer.tab_icts": "ICTS Compliance",
    "nexteer.tab_usmca": "USMCA & RVC",
    "nexteer.tab_relocation": "Relocation Tracker",
    "nexteer.tab_erp": "ERP Integration",

    // Overview tab
    "nexteer.rfc_help": "Enter supplier RFCs to see EFOS 69-B screening results. Try: HSC780901FFF (flagged) or FPQ870315AAA (clean). SZS190820CCC is clean but high risk (ICTS).",
    "nexteer.stat_icts_exposure": "ICTS Exposure",
    "nexteer.stat_icts_sub": "component(s)",
    "nexteer.stat_rvc_margin": "RVC Margin",
    "nexteer.stat_rvc_margin_sub": "pts above threshold",
    "nexteer.stat_relocations": "Relocations Active",
    "nexteer.stat_relocations_sub": "in transition",
    "nexteer.stat_mv_ready": "MV Ready",
    "nexteer.overview_title": "Supplier Compliance Overview",
    "nexteer.th_icts": "ICTS",
    "nexteer.th_relocation": "Relocation",
    "nexteer.th_efos": "EFOS",
    "nexteer.status_established": "Established",
    "nexteer.remediation_history": "Remediation History",
    "nexteer.remediation_resolved": "ICTS MCU issue resolved via {path} path",

    // ICTS tab
    "nexteer.icts_dashboard": "ICTS Rule Status Dashboard",
    "nexteer.icts_software_deadline": "MY 2027 Software Deadline",
    "nexteer.icts_hardware_deadline": "MY 2030 Hardware Deadline",
    "nexteer.icts_declaration": "Declaration of Conformity",
    "nexteer.icts_not_filed": "Not yet filed",
    "nexteer.icts_bis_advisory": "BIS Advisory Opinion",
    "nexteer.icts_not_requested": "Not requested",
    "nexteer.icts_matrix_title": "Component Classification Matrix",
    "nexteer.th_covered_software": "Contains Covered Software?",
    "nexteer.th_vcs_hardware": "Contains VCS Hardware?",
    "nexteer.th_icts_risk": "ICTS Risk Level",
    "nexteer.th_notes": "Notes",
    "nexteer.icts_eps": "Electric Power Steering (EPS)",
    "nexteer.icts_eps_note": "Core product most exposed",
    "nexteer.icts_sbw": "Steer-by-Wire",
    "nexteer.icts_sbw_note": "Safety-critical, highest scrutiny",
    "nexteer.icts_columns": "Steering Columns",
    "nexteer.icts_columns_note": "Mostly mechanical unless electric tilt/telescope",
    "nexteer.icts_shafts": "Intermediate Shafts",
    "nexteer.icts_shafts_note": "Purely mechanical",
    "nexteer.icts_driveline": "Halfshafts/Driveline",
    "nexteer.icts_bbw": "Brake-by-Wire",
    "nexteer.icts_bbw_note": "New product line, same ICTS exposure",
    "nexteer.icts_audit_title": "Supply Chain ICTS Audit",
    "nexteer.th_country_design": "Country of Design",
    "nexteer.th_country_mfg": "Country of Manufacture",
    "nexteer.th_avic_nexus": "AVIC Nexus",
    "nexteer.th_mitigation": "Mitigation Status",
    "nexteer.avic_yes": "YES",
    "nexteer.avic_no": "NO",
    "nexteer.mitigation_accelerated": "Accelerated relocation — 100% Mexico sourcing",
    "nexteer.mitigation_unmitigated": "UNMITIGATED — action required",
    "nexteer.icts_does_1": "Monitor BIS rulemaking and advisory opinions",
    "nexteer.icts_does_2": "Track component-level supply chain for ICTS-covered products",
    "nexteer.icts_does_3": "Map sub-tier supplier ownership to foreign adversary designations",
    "nexteer.icts_does_4": "Alert when supplier ownership or manufacturing location changes",
    "nexteer.icts_does_5": "Maintain ICTS audit trail for Declaration of Conformity support",
    "nexteer.icts_not_1": "Provide legal opinions on whether transactions are prohibited",
    "nexteer.icts_not_2": "File BIS advisory opinion requests",
    "nexteer.icts_not_3": "Make legal determinations on 'covered software' classification",
    "nexteer.icts_not_4": "Replace trade counsel for ICTS compliance strategy",
    "nexteer.icts_footer": "Alisio provides the data and monitoring — not the legal determination. For legal interpretation, consult trade counsel.",

    // USMCA & RVC tab
    "nexteer.rvc_dashboard": "Regional Value Content by Product Line",
    "nexteer.core_part": "core part",
    "nexteer.principal_part": "principal part",
    "nexteer.complementary_part": "complementary part",
    "nexteer.margin": "margin",
    "nexteer.core_part_title": "USMCA Core Part Classification",
    "nexteer.core_part_desc": "Under USMCA, steering systems are one of seven 'core parts' that must independently meet the 75% RVC threshold. This is the highest threshold in the agreement.",
    "nexteer.th_category": "Category",
    "nexteer.th_threshold": "Threshold",
    "nexteer.th_nexteer_products": "Nexteer Products",
    "nexteer.cat_core": "Core Part (S4-B.3)",
    "nexteer.cat_principal": "Principal Part",
    "nexteer.cat_complementary": "Complementary Part",
    "nexteer.products_core": "EPS, Steer-by-Wire, Steering Columns",
    "nexteer.products_principal": "Halfshafts, Driveline joints",
    "nexteer.products_complementary": "Intermediate shafts, Universal joints",
    "nexteer.rvc_calc_title": "RVC Calculator (Net Cost Method)",
    "nexteer.rvc_calc_desc": "RVC = ((NC - VNM) / NC) x 100 where NC = Net Cost and VNM = Value of Non-Originating Materials. Pre-populated with EPS assembly data.",
    "nexteer.net_cost_label": "Net Cost (MXN)",
    "nexteer.vnm_label": "Non-Originating Materials (MXN)",
    "nexteer.calculate_rvc": "Calculate RVC",
    "nexteer.calculated_rvc": "Calculated RVC (Net Cost Method)",
    "nexteer.steel_origin_title": "Steel/Aluminum Origin Verification (Article 4.6)",
    "nexteer.steel_origin_desc": "70% of steel must be melted and poured in a USMCA country. For Nexteer's forging stock, this means tracing the billet back to the melt shop.",
    "nexteer.th_steel_grade": "Steel Grade",
    "nexteer.th_mill_source": "Mill Source",
    "nexteer.th_usmca_origin": "USMCA Origin",
    "nexteer.th_melt_pour": "Melt-and-Pour",
    "nexteer.joint_review_title": "Joint Review Monitor",
    "nexteer.joint_review_event_1": "USITC launches investigation into USMCA auto rules of origin",
    "nexteer.joint_review_event_2": "EPI testimony flags Chinese-affiliated firms",
    "nexteer.joint_review_event_3": "Public hearings expected",
    "nexteer.joint_review_event_4": "Formal Joint Review begins",
    "nexteer.joint_review_proposals": "Key proposals under consideration:",
    "nexteer.proposal_1": "Threshold increases (core parts from 75% to 80%)",
    "nexteer.proposal_2": "Chinese content scrutiny and tracing requirements",
    "nexteer.proposal_3": "Roll-up provision changes",
    "nexteer.impact_title": "Impact Modeling: Threshold Increase to 80%",
    "nexteer.impact_desc": "If the threshold increases from 75% to 80%, EPS (78.2%) and Steer-by-Wire (76.1%) would FAIL. Requires origin improvement for Suzhou components.",
    "nexteer.rollup_title": "Roll-Up Provision (2023 Panel Dispute)",
    "nexteer.rollup_p1": "The 2023 panel dispute: Mexico/Canada argued core parts meeting 75% get full value counted as originating at vehicle level. US disagreed. Panel sided with Mexico/Canada.",
    "nexteer.rollup_p2": "If overturned in 2026 review, OEMs would need to trace non-originating materials through Nexteer's products to vehicle level. This would significantly increase documentation requirements and could impact Nexteer's commercial position with OEM customers.",
    "nexteer.s232_title": "Section 232 — Alloy Steel Content",
    "nexteer.s232_desc": "Nexteer uses alloy steel for forgings. 232 applies to the steel content, but forging + machining + assembly adds enough value that finished steering components typically qualify under USMCA and avoid 232.",
    "nexteer.s232_monthly": "Monthly steel content (MXN)",
    "nexteer.s232_verified": "Verified USMCA origin",
    "nexteer.s232_exposure": "232 exposure (unverified)",

    // Steering system definition (new content per prompt)
    "nexteer.steering_definition": "The steering system is defined by USMCA as one of seven core parts: engine, transmission, body and chassis, axle, steering system, suspension system, and advanced battery. Components include steering columns, steering gear/racks, and control units. Each must independently meet the 75% RVC threshold.",
    "nexteer.super_core": "USMCA provides flexibility in HOW steering RVC is calculated. Nexteer can calculate at the full steering system level, at the enumerated component level, or across all core parts together. Alisio's RVC calculator models all methods and shows which gives the best result.",
    "nexteer.rollup_status": "The 2023 dispute panel ruled that core parts meeting 75% RVC independently may count as 100% originating at the vehicle level ('roll-up'). The US contested this. It remains a key issue in the 2026 Joint Review. If overturned, non-originating content inside Nexteer's steering (Chinese-origin MCUs, non-USMCA steel) would trace through to the vehicle level.",
    "nexteer.steel_2027": "Starting July 2027 (7 years after USMCA entry into force), steel may only qualify as originating if ALL steel manufacturing processes occur in USMCA countries. Nexteer's forged steel components (SAE 4140/4340 rack bars, pinion shafts) need steel entirely melted and poured in USMCA countries.",
    "nexteer.halfshafts_note": "Nexteer's driveline products (halfshafts, propeller shaft joints) classify under 8708.50 — Table B (principal parts) at 70%, NOT Table A (core parts) at 75%. The compliance pressure is lower on driveline than steering.",

    // ECU classification dispute (new content per prompt)
    "nexteer.ecu_dispute_alert": "ECU classification disputed — if classified under 8537 (Ch.85) instead of 8708.94 (Ch.87), this component falls outside the steering system core part calculation. This changes the math for the 75% threshold.",

    // Relocation tab
    "nexteer.relocation_title": "Relocation Status Board",
    "nexteer.relocation_suppliers": "13 suppliers",
    "nexteer.stage_planning": "Planning",
    "nexteer.stage_establishing": "Establishing",
    "nexteer.stage_qualifying": "Qualifying",
    "nexteer.stage_transitioning": "Transitioning",
    "nexteer.stage_complete": "Complete",
    "nexteer.th_from": "From",
    "nexteer.th_to": "To",
    "nexteer.th_stage": "Stage",
    "nexteer.th_mexico_pct": "Mexico %",
    "nexteer.th_usmca_impact": "USMCA Impact",
    "nexteer.th_icts_impact": "ICTS Impact",
    "nexteer.mixed_origin_title": "Mixed-Origin Risk During Transition",
    "nexteer.mixed_origin_desc": "During relocation transitions, suppliers ship from both Chinese and Mexican facilities. This creates a dual-sourcing documentation challenge: compliance documentation must track origin per shipment, not per supplier.",
    "nexteer.mixed_origin_warning": "When a supplier ships China-origin material during transition, compliance documentation must track origin per shipment — not per supplier. Each receiving dock scan must verify the actual plant of manufacture to correctly calculate RVC for that production lot.",
    "nexteer.mixed_origin_clear": "No suppliers currently in dual-source transitioning stage. Mixed-origin documentation risk is minimal.",
    "nexteer.aggregate_rvc_title": "Aggregate RVC Impact — EPS Assembly",
    "nexteer.current_rvc": "Current RVC (EPS)",
    "nexteer.projected_rvc": "Projected RVC (all 13 relocations complete)",
    "nexteer.remediated": "remediated",

    // Suppliers tab
    "nexteer.icts_risk": "ICTS Risk",
    "nexteer.relocation_progress": "Relocation Progress",
    "nexteer.rvc_contribution": "RVC Contribution",
    "nexteer.resolve_icts": "Resolve ICTS Issue",

    // Alerts
    "nexteer.alert_resolved_title": "Resolved — ICTS MCU Issue (Suzhou Steering Components de México)",
    "nexteer.alert_resolved_detail": "Remediated via {path} path — AVIC subsidiary dependency eliminated, GM MY 2027 compliance restored",

    // Option 1 nexteer-specific
    "nexteer.opt1_step1_detail": "Mill certificates, invoices, origin declarations sent to verificacion@nexteer.com. Same documents they already produce, different address.",
    "nexteer.opt1_step2_detail": "Transaction values cross-referenced vs SAT. RFC screened vs 69-B. HS codes validated. Steel pricing checked vs LME. ICTS component origins flagged.",
    "nexteer.opt1_step3_detail": "Pre-filled compliance certificate with verified data. Issues flagged for Nexteer review before sending to supplier.",
    "nexteer.opt1_catch_efos_title": "EFOS — Hules y Sellos del Centro",
    "nexteer.opt1_catch_efos_detail": "Hules y Sellos del Centro on 69-B presumido. Option 1 catches this the day the supplier is listed — before their next invoice hits a pedimento.",
    "nexteer.opt1_catch_origin_title": "Origin — Jiangsu Auto Parts MTY",
    "nexteer.opt1_catch_origin_detail": "Jiangsu mill cert shows Chinese steel — doesn't count for USMCA Article 4.6. At 50% Section 232, each unverified shipment is direct cost exposure.",
    "nexteer.opt1_catch_icts_title": "ICTS — Suzhou Steering Components",
    "nexteer.opt1_catch_icts_unresolved": "Sub-tier electronic component in Suzhou ECU has undisclosed AVIC manufacturing origin. Falls under ICTS connected-vehicle prohibition for GM MY 2027 platforms.",
    "nexteer.opt1_catch_icts_resolved": "Resolved — AVIC subsidiary dependency eliminated. MCU sourcing redirected to compliant supplier. ICTS documentation package assembled.",
    "nexteer.opt1_see_remediation": "See remediation options",
    "nexteer.opt1_catch_value_title": "Value — Relocated supplier pricing",
    "nexteer.opt1_catch_value_detail": "Relocated supplier invoicing at China-level pricing — suggests re-invoicing rather than actual Mexico manufacturing. Transaction value anomaly flagged for review.",
    "nexteer.opt1_liability_icts": "For ICTS compliance, Nexteer must submit annual Declarations of Conformity. The compliance certificate from Option 1 provides per-supplier, per-component documentation that supports this annual declaration.",
    "nexteer.opt1_erp_note_title": "ERP Integration:",
    "nexteer.opt1_erp_note_detail": "Read-only API connection to supplier master and purchasing data. 1-2 days of configuration. See the ERP Integration tab for full technical details.",

    // Option 2 nexteer-specific
    "nexteer.opt2_key_message": "For compliance verification, Option 1 is all Nexteer needs. Option 2 is the incentive that motivates some suppliers to go deeper voluntarily. Suppliers who want the benefit opt in — no mandate required.",

    // ERP tab nexteer-specific
    "nexteer.erp_title": "ERP Integration",
    "nexteer.erp_detail": "Read-only. Lightweight. Works with SAP, Oracle, or any modern ERP.",
    "nexteer.erp_case2_detail": "Alisio verifies Nexteer's sub-suppliers using data already in Nexteer's ERP. Sub-suppliers don't grant any access.",
    "nexteer.erp_integration_value": "API integration (REST or OData)",
    "nexteer.erp_case1_title": "Case 1: Verify Nexteer's Own Compliance",
    "nexteer.erp_case1_detail": "Nexteer verifies its own CFDI issuance, tax standing, and USMCA documentation for OEM customers.",
    "nexteer.erp_case1_1": "Nexteer's issued CFDIs",
    "nexteer.erp_arch_erp": "Nexteer ERP",
    "nexteer.erp_arch_erp_sub": "SAP, Oracle, or equivalent",
    "nexteer.erp_arch_rest": "REST / OData integration",
    "nexteer.erp_arch_portal": "/nexteer-portal",
    "nexteer.erp_qa_platform": "ERP platform",
    "nexteer.erp_qa_platform_answer": "TBD (SAP, Oracle, or equivalent)",
    "nexteer.erp_qa_setup_case2": "Setup (Case 2)",
    "nexteer.erp_qa_setup_both": "Setup (Case 1+2)",

    // Digital Passport
    "nexteer.passport_title": "Digital Product Passport",
    "nexteer.passport_desc": "Per-unit compliance snapshot for a single EPS assembly. Combines supplier verification, origin tracing, and regulatory status into one auditable record.",
    "nexteer.passport_product_title": "Product Passport — EPS Unit KAT-EPS-2026-0112",
    "nexteer.passport_product": "Product",
    "nexteer.passport_product_value": "Electric Power Steering Unit",
    "nexteer.passport_assembly": "Assembly",
    "nexteer.passport_assembly_value": "Nexteer QRO Plant 1",
    "nexteer.passport_components": "Components",
    "nexteer.passport_tracked": "tracked",
    "nexteer.passport_verification": "Alisio Verification Overlay",
    "nexteer.passport_rvc": "Regional Value Content (RVC)",
    "nexteer.passport_rvc_above": "Above 75% USMCA threshold",
    "nexteer.passport_rvc_below": "Below 75% USMCA threshold",
    "nexteer.passport_icts_status": "ICTS Status",
    "nexteer.passport_icts_compliant": "Compliant",
    "nexteer.passport_icts_at_risk": "At risk — AVIC dependency",
    "nexteer.passport_steel_origin": "Steel Origin",
    "nexteer.passport_compliance_score": "Compliance Score",
    "nexteer.passport_excellent": "Excellent",
    "nexteer.passport_good": "Good — issues flagged",
    "nexteer.passport_needs_attention": "Needs attention",

    // Remediation workflow
    "nexteer.remediation_title": "ICTS Remediation — Suzhou Steering Components",
    "nexteer.remediation_desc": "ECU subassembly contains microcontroller manufactured by AVIC subsidiary. Three remediation paths available.",
    "nexteer.remediation_current_state": "Current State",
    "nexteer.remediation_supplier": "Supplier:",
    "nexteer.remediation_component": "Component:",
    "nexteer.remediation_component_value": "Torque sensor + ECU subassembly for EPS",
    "nexteer.remediation_issue": "Issue:",
    "nexteer.remediation_issue_value": "MCU manufactured by AVIC subsidiary — ICTS prohibited transaction for MY 2027 connected vehicles",
    "nexteer.remediation_sourcing": "Current sourcing:",
    "nexteer.remediation_sourcing_value": "60% Mexico / 40% China",
    "nexteer.remediation_rvc_impact": "Current RVC impact:",
    "nexteer.remediation_rvc_value": "EPS at 78.2% (3.2 pts above threshold)",
    "nexteer.remediation_oem": "OEM exposure:",
    "nexteer.remediation_oem_value": "GM connected vehicles, MY 2027 deadline",
    "nexteer.remediation_paths": "Remediation Paths",
    "nexteer.path_accelerate": "Accelerate Relocation",
    "nexteer.path_recommended": "RECOMMENDED",
    "nexteer.path_accelerate_desc": "Move to 100% Mexico sourcing. Eliminates both ICTS and RVC risk simultaneously.",
    "nexteer.path_timeline": "Timeline:",
    "nexteer.path_accelerate_timeline": "6 weeks",
    "nexteer.path_icts": "ICTS:",
    "nexteer.path_accelerate_icts": "ICTS risk eliminated — no Chinese-manufactured components",
    "nexteer.path_rvc": "RVC:",
    "nexteer.path_accelerate_rvc": "EPS RVC increases from 78.2% to 83.4% (+5.2 pts)",
    "nexteer.path_cost": "Cost:",
    "nexteer.path_accelerate_cost": "Transition costs: ~$120K USD (tooling, qualification, logistics)",
    "nexteer.path_select": "Select This Path",
    "nexteer.path_mcu": "Substitute MCU",
    "nexteer.path_mcu_desc": "Replace AVIC-manufactured MCU with alternative (Texas Instruments, NXP). Requires ECU requalification.",
    "nexteer.path_mcu_timeline": "8-12 weeks",
    "nexteer.path_mcu_icts": "ICTS risk eliminated for ECU specifically",
    "nexteer.path_mcu_rvc": "Minimal change — MCU cost is small portion of ECU value",
    "nexteer.path_mcu_cost": "Engineering: ~$200K USD (redesign, qualification, validation)",
    "nexteer.path_bis": "BIS Specific Authorization",
    "nexteer.path_bis_desc": "Request authorization from BIS for continued use during transition period.",
    "nexteer.path_bis_timeline": "60+ days for advisory opinion, no guarantee",
    "nexteer.path_bis_icts": "Temporary relief if granted — not a permanent solution",
    "nexteer.path_bis_rvc": "No change",
    "nexteer.path_bis_cost": "Legal fees: ~$50-80K USD",
    "nexteer.path_selected": "Selected",
    "nexteer.cascade_1": "Suzhou Mexico operations → 100% capacity",
    "nexteer.cascade_2": "China sourcing → terminated",
    "nexteer.cascade_3": "ICTS audit → MCU removed from supply chain",
    "nexteer.cascade_4": "RVC recalculation → EPS 83.4% (+5.2 pts)",

    // Regulatory scenario toggle
    "nexteer.scenario_current": "Current Rules (2026)",
    "nexteer.scenario_jr2026": "JR 2026 — Proposed",
    "nexteer.scenario_2027": "2027 Steel + ICTS",
    "nexteer.scenario_jr2026_desc": "Core 75%→80%. Roll-up overturned — non-originating content traces to vehicle. Chinese-affiliation disclosure (>25% ownership).",
    "nexteer.scenario_jr2026_basis": "USITC Investigation 332-596, EPI Congressional testimony (Mar 2026), FTA-MX-USA-2022-8708-01 appeal",
    "nexteer.scenario_2027_desc": "All JR 2026 changes + steel ALL processes in USMCA (Art. 4.6 tightening Jul 2027) + MY 2030 ICTS hardware prohibition effective.",
    "nexteer.scenario_2027_basis": "USMCA Article 4.6 (7-year transition), ICTS Final Rule §7.100(b) hardware provisions",
    "nexteer.scenario_rollup_overturned_title": "Roll-up provision overturned.",
    "nexteer.scenario_rollup_overturned_detail": "Non-originating content inside Nexteer steering products now traces through to the vehicle-level RVC calculation. OEMs (e.g., GM) must account for Nexteer's non-originating material in their own 75% vehicle-level threshold. This changes Nexteer's commercial position significantly.",
    "nexteer.scenario_rollup_traces": "Roll-up overturned: {pct}% non-originating content traces to vehicle level",
    "nexteer.scenario_affiliation_title": "Chinese-affiliation disclosure triggered.",
    "nexteer.scenario_affiliation_detail": "Nexteer's ultimate beneficial owner (AVIC/China Aviation Industry Corporation) holds >{threshold}% ownership. Under proposed rules, Tier 1 suppliers with foreign adversary nation ownership above this threshold must disclose this to OEM customers and CBP. This does not change RVC math — it creates an additional reporting obligation and may affect OEM sourcing decisions.",
    "nexteer.scenario_steel_title": "Steel origin tightening (July 2027).",
    "nexteer.scenario_steel_detail": "ALL steel manufacturing processes must occur in USMCA countries — not just melt-and-pour. Forged steel components (SAE 4140/4340 rack bars, pinion shafts) need the entire manufacturing chain verified. Any supplier using imported billets or slabs loses USMCA origin qualification even if melting occurred in-region.",
    "nexteer.scenario_icts_hw_title": "ICTS hardware prohibition (MY 2030).",
    "nexteer.scenario_icts_hw_detail": "Physical hardware components (sensors, actuators, ECUs) from foreign adversary nation manufacturers are prohibited in connected vehicles. Extends beyond software to all VCS hardware. All Nexteer EPS and Steer-by-Wire assemblies must have fully compliant hardware supply chains.",
    "nexteer.scenario_accelerated": "Accelerated remediation applied.",
    "nexteer.scenario_accelerated_detail": "Suzhou content shifted to 100% Mexico sourcing. EPS improved from 78.2% to 83.4%, Steer-by-Wire from 76.1% to 81.8%.",

    // Email demo nexteer-specific
    "nexteer.email_icts": "ICTS",
    "nexteer.email_origin": "Origin",
    "nexteer.email_seed": "seed",
    "nexteer.cert_fail_steel": "Steel origin: Not USMCA originating. Melted and poured in China. Section 232 exposure applies.",
    "nexteer.cert_warn_icts": "MCU sourced from AVIC subsidiary — ICTS connected-vehicle review required before certificate issuance.",
    "nexteer.cert_warn_review": "field(s) flagged for Nexteer review before sending to supplier.",
    "nexteer.cert_fail_msg": "field(s) failed verification. Certificate cannot be issued until origin compliance is resolved.",
    "nexteer.manual_review": "Manual review: est. 45-90 minutes per email",
  },

  // ===========================================================================
  // SPANISH (MEXICAN)
  // ===========================================================================
  es: {
    // PORTAL SHARED
    "portal.demo_banner_prefix": "Modo Demo",
    "portal.reset_demo": "Reiniciar Demo",
    "portal.supplier_compliance_portal": "Portal de Cumplimiento de Proveedores",
    "portal.mv_deadline": "Plazo MV: 1 de junio",
    "portal.days": "días",
    "portal.days_remaining": "días restantes",

    "portal.tab_overview": "Resumen General",
    "portal.tab_suppliers": "Proveedores",
    "portal.tab_alerts": "Alertas",
    "portal.tab_option1": "Opción 1: Verificación",
    "portal.tab_option2": "Opción 2: Pago Anticipado",
    "portal.tab_digital_passport": "Pasaporte Digital",

    "portal.th_supplier": "Proveedor",
    "portal.th_status": "Estado",
    "portal.th_risk": "Riesgo",
    "portal.th_component": "Componente",
    "portal.th_material": "Material",
    "portal.th_rfc": "RFC",
    "portal.th_hs_code": "Código HS",
    "portal.th_mv_status": "Estado MV",
    "portal.th_question": "Pregunta",
    "portal.th_answer": "Respuesta",
    "portal.th_monthly_volume": "Volumen Mensual",
    "portal.th_connection": "Conexión",
    "portal.th_compliance": "Cumplimiento",
    "portal.th_terms": "Términos",

    "portal.status_clean": "Limpio",
    "portal.status_flagged": "Marcado",
    "portal.status_pending": "Pendiente",
    "portal.status_verified": "Verificado",
    "portal.status_missing": "Faltante",
    "portal.status_blocked": "BLOQUEADO",
    "portal.status_active": "ACTIVO",
    "portal.status_eligible": "ELEGIBLE",
    "portal.status_not_eligible": "NO ELEGIBLE",
    "portal.status_exempt": "EXENTO",
    "portal.status_match": "Coincide",
    "portal.status_mismatch": "No coincide",
    "portal.status_on_file": "En archivo",
    "portal.status_ready": "Listo",
    "portal.status_pass": "APROBADO",
    "portal.status_fail": "RECHAZADO",
    "portal.status_updated": "Actualizado",
    "portal.status_updating": "Actualizando...",

    "portal.risk_low": "Bajo",
    "portal.risk_medium": "Medio",
    "portal.risk_high": "Alto",
    "portal.risk_critical": "Crítico",
    "portal.risk_none": "Ninguno",

    "portal.efos_label": "EFOS:",
    "portal.cfdi_label": "CFDI:",
    "portal.mill_cert_label": "Certificado de Molino:",
    "portal.steel_label": "Acero:",
    "portal.mv_label": "MV:",
    "portal.rfc_label": "RFC:",
    "portal.unknown": "Desconocido",
    "portal.yes": "Sí",
    "portal.no": "No",
    "portal.none": "Ninguno",

    "portal.download_results": "Descargar Resultados",
    "portal.export_pdf": "Exportar PDF",
    "portal.try_another": "Probar otro",
    "portal.close_return": "Cerrar y Volver al Portal",

    "portal.rfc_screening_title": "Pruébalo: Filtra a Tus Proveedores",
    "portal.rfc_textarea_placeholder": "Ingresa RFCs — uno por línea o separados por coma",
    "portal.rfc_screening_progress": "Filtrando RFC",
    "portal.run_screening": "Ejecutar Filtrado",
    "portal.clean_demo": "Limpio (modo demo)",

    // Option 1 shared
    "portal.opt1_title": "Opción 1: Verificación de Cumplimiento",
    "portal.opt1_step1_title": "Proveedor envía documentos",
    "portal.opt1_step2_title": "Alisio analiza y verifica",
    "portal.opt1_step2_detail": "Valores de transacción cruzados con SAT. RFC filtrado contra 69-B. Códigos HS validados. Precio del acero verificado contra LME.",
    "portal.opt1_step3_title": "Certificado generado",
    "portal.opt1_step4_title": "Proveedor firma",
    "portal.opt1_step4_detail": "Firma digital = certificación formal. Registro de debida diligencia documentada creado. Documento timbrado y listo para MV.",
    "portal.opt1_what_catches": "Qué Detecta la Opción 1",
    "portal.opt1_see_it_work": "Verlo en acción",

    "portal.liability_today_title": "HOY: Responsabilidad solidaria (obligado solidario), datos sin verificar",
    "portal.liability_today_detail": "Los datos de sub-proveedores van al pedimento y MV sin certificación formal. Bajo la reforma 2026, tanto importador como agente aduanal comparten responsabilidad solidaria (obligado solidario) por cada valor declarado. La exención anterior del agente aduanal por datos inexactos proporcionados por el cliente ha sido eliminada. Sin rastro de debida diligencia documentada. La respuesta ante auditoría requiere reconstruir documentación de fuentes dispersas.",
    "portal.liability_opt1_title": "CON OPCIÓN 1: Debida diligencia documentada por transacción",
    "portal.liability_opt1_detail": "Cada transacción tiene un certificado de cumplimiento firmado. El proveedor certifica formalmente sus valores y origen. Verificado contra SAT antes de la presentación. La respuesta ante auditoría es inmediata — obtener el Expediente Digital de Cumplimiento. Crea el registro de verificación documentada que la reforma 2026 requiere que las empresas IMMEX mantengan, y proporciona una base contractual para la rendición de cuentas de proveedores.",

    // Option 2 shared
    "portal.opt2_title": "Opción 2: Programa de Pago Anticipado",
    "portal.opt2_detail": "Se basa en la Opción 1. Los sub-proveedores que desean cobrar más rápido conectan voluntariamente sus sistemas y son pre-calificados. Los datos de cumplimiento verificados desbloquean el pago anticipado. El incentivo es dinero, no un mandato.",
    "portal.opt2_opted_in": "Proveedores Inscritos",
    "portal.opt2_acceleration": "Aceleración de Pago",
    "portal.opt2_prerequisite": "Prerrequisito: el cumplimiento debe aprobarse",
    "portal.opt2_table_title": "Estado de Pago Anticipado por Proveedor",
    "portal.opt2_failed": "Falló",
    "portal.opt2_erp_connected": "ERP Conectado",
    "portal.opt2_email_only": "Solo Correo",
    "portal.opt2_step1_title": "Aprobar cumplimiento",
    "portal.opt2_step1_detail": "Ya en Opción 1, enviando documentos, firmando certificados.",
    "portal.opt2_step2_title": "Proveedor se inscribe",
    "portal.opt2_step2_detail": "Conecta voluntariamente su ERP/comparte CIEC. AQUÍ es donde el acceso al ERP es relevante.",
    "portal.opt2_step3_title": "Pago anticipado activado",
    "portal.opt2_step3_detail": "NET 15 en lugar de NET 60. Financiado a través de la capa de financiamiento de Alisio.",

    // ERP/SAP shared
    "portal.erp_recommended": "PUNTO DE PARTIDA RECOMENDADO",
    "portal.erp_phase2": "FASE 2 — OPCIONAL",
    "portal.erp_case2_title": "Caso 2: Verificar a Tus Proveedores",
    "portal.erp_case2_data_header": "Datos leídos del ERP:",
    "portal.erp_case2_data_1": "Datos maestros de proveedor (RFC, nombre, ID proveedor)",
    "portal.erp_case2_data_2": "Referencias de órdenes de compra y recepción de mercancía",
    "portal.erp_case2_data_3": "UUIDs de CFDI entrantes (folio fiscal)",
    "portal.erp_data_objects": "Objetos de Datos",
    "portal.erp_data_objects_value": "Maestro de Proveedores, Compras, CP (lectura)",
    "portal.erp_integration": "Integración",
    "portal.erp_auth": "Autenticación",
    "portal.erp_auth_value": "Certificado u OAuth",
    "portal.erp_setup": "Configuración",
    "portal.erp_setup_1_2_days": "1-2 días",
    "portal.erp_case1_data_header": "Datos adicionales más allá del Caso 2:",
    "portal.erp_case1_data_1": "CFDIs emitidos",
    "portal.erp_case1_data_2": "Datos de contabilidad de costos (para CVR Costo Neto)",
    "portal.erp_case1_data_3": "Estructuras de lista de materiales (BOM)",
    "portal.erp_case1_data_4": "Datos propios de RFC/fiscal",
    "portal.erp_additional_data": "Datos Adicionales",
    "portal.erp_additional_data_value": "Documentos Financieros, BOM, Costos (lectura)",
    "portal.erp_setup_2_3_days": "2-3 días (alcance más amplio)",
    "portal.erp_not_required_title": "Lo Que Alisio NO Requiere",
    "portal.erp_not_required_1": "Sin código personalizado ni extensiones",
    "portal.erp_not_required_2": "Sin cambios a roles o permisos existentes",
    "portal.erp_not_required_3": "Sin acceso de escritura a ningún objeto de datos",
    "portal.erp_not_required_4": "Sin modificaciones a flujos de trabajo o pantallas existentes",
    "portal.erp_not_required_5": "Sin acceso a entorno de desarrollo",
    "portal.erp_arch_title": "Arquitectura",
    "portal.erp_arch_read_only": "API de Solo Lectura",
    "portal.erp_arch_alisio": "Verificación Alisio",
    "portal.erp_arch_alisio_sub": "Analizar, validar, calcular",
    "portal.erp_arch_sat": "SAT / SW SmartWeb",
    "portal.erp_arch_sat_sub": "Referencia cruzada",
    "portal.erp_arch_dashboard": "Panel de Cumplimiento",
    "portal.erp_arch_footer": "El trabajo pesado ocurre Alisio-SAT, no Alisio-ERP.",
    "portal.erp_quick_ref": "Referencia Rápida",
    "portal.erp_qa_custom_code": "¿Código personalizado?",
    "portal.erp_qa_write_access": "¿Acceso de escritura?",
    "portal.erp_qa_auth": "Método de autenticación",
    "portal.erp_qa_impact": "Impacto en usuarios",

    // Email demo shared
    "portal.email_demo_title": "Pruébalo: Procesar un Correo de Proveedor",
    "portal.email_demo_detail": "Selecciona un correo de ejemplo y observa cómo Alisio analiza, verifica y genera un certificado de cumplimiento en tiempo real.",
    "portal.pipeline_receiving": "Recibiendo",
    "portal.pipeline_parsing": "Analizando",
    "portal.pipeline_verifying": "Verificando",
    "portal.pipeline_certificate": "Certificado",
    "portal.phase_receiving": "Recibiendo correo...",
    "portal.phase_extracting": "Extrayendo campos...",
    "portal.phase_verifying": "Verificando contra fuentes externas...",
    "portal.phase_complete": "Verificación completa",
    "portal.cert_title": "Certificado de Cumplimiento — Listo para Firma",
    "portal.cert_all_verified": "TODO VERIFICADO",
    "portal.cert_supplier": "Proveedor:",
    "portal.cert_id": "ID del Certificado:",
    "portal.cert_fields_verified": "Campos verificados:",
    "portal.cert_generated": "Generado:",
    "portal.cert_signature_line": "Línea de firma digital",
    "portal.cert_signature_detail": "El proveedor firma aquí — registro de debida diligencia documentada creado — documento timbrado y listo para MV",
    "portal.cert_issues_flagged": "problema(s) marcado(s)",
    "portal.cert_processing_time": "Tiempo de procesamiento:",

    "portal.field_rfc_emisor": "RFC Emisor",
    "portal.field_cfdi_uuid": "CFDI UUID",
    "portal.field_invoice_total": "Total de Factura",
    "portal.field_hs_declared": "Código HS Declarado",
    "portal.field_steel_supplier": "Proveedor de Acero",
    "portal.field_heat_lot": "Lote de Fundición",
    "portal.field_melted_poured": "Fundido y Vaciado",
    "portal.field_material_grade": "Grado de Material",
    "portal.field_lme_price": "Precio Acero vs LME",

    "portal.verify_efos": "EFOS 69-B vía SW SmartWeb",
    "portal.verify_sat_soap": "Endpoint SOAP del SAT",
    "portal.verify_alisio_hs": "Motor de Validación HS de Alisio",
    "portal.verify_supplier_master": "Referencia cruzada de maestro de proveedores",
    "portal.verify_mill_cert_db": "Base de datos de certificados de molino",
    "portal.verify_usmca_origin": "Determinación de origen T-MEC",
    "portal.verify_spec_match": "Coincidencia de especificaciones vs OC",
    "portal.verify_lme_benchmark": "Comparación con referencia LME",

    "portal.result_clean_efos": "Limpio — no en lista 69-B",
    "portal.result_vigente": "Vigente — válido y activo",
    "portal.result_supplier_verified": "Verificado — molino originario T-MEC conocido",
    "portal.result_usmca_origin": "Originario T-MEC — fundido y vaciado en México",
    "portal.result_spec_match": "Coincide — dentro de especificación",
    "portal.result_lme_normal": "Dentro de rango — normal",
    "portal.result_heat_lot_valid": "Válido — coincide con registros",

    "portal.what_alisio_does": "Qué Hace Alisio",
    "portal.what_alisio_does_not": "Qué NO Hace Alisio",

    "portal.mv_banner_title": "Manifestación de Valor — Presentación Electrónica Obligatoria 1 de Junio de 2026",
    "portal.mv_shared_liability": "Responsabilidad solidaria (obligado solidario)",
    "portal.mv_fines": "Multas: hasta MXN $53,500 por operación",
    "portal.mv_false_declarations": "Declaraciones falsas: 70-250% del valor de la mercancía",

    // =========================================================================
    // KATCON SPANISH
    // =========================================================================
    "katcon.demo_banner": "Modo Demo — Mostrando datos representativos de la cadena de suministro de Katcon. Conecte su SAP para datos en vivo.",
    "katcon.tab_customs": "Aduanas y 232",
    "katcon.tab_sap": "Integración SAP",

    "katcon.rfc_help": "Ingrese RFCs de proveedores para ver resultados de filtrado EFOS 69-B. Pruebe: FIC780901EEE (marcado) o FMB860101AAA (limpio).",
    "katcon.stat_hs_issues": "Problemas de Código HS",
    "katcon.stat_hs_issues_sub": "de {count} proveedores",
    "katcon.stat_mv_ready": "Listo para MV",
    "katcon.stat_232_exposure": "Exposición Sección 232",
    "katcon.stat_232_at_risk": "MXN — aranceles en riesgo",
    "katcon.stat_232_clear": "Sin exposición",
    "katcon.stat_rvc_fmb": "CVR (FMB)",
    "katcon.stat_rvc_threshold": "Umbral: 65%",
    "katcon.overview_title": "Resumen de Cumplimiento de Proveedores",
    "katcon.th_hs_status": "Estado HS",
    "katcon.th_232_exposure": "Exposición 232",
    "katcon.correction_history": "Historial de Correcciones",

    "katcon.hs_classification": "Clasificación HS",
    "katcon.section_232": "Sección 232",
    "katcon.apply_correction": "Aplicar Corrección",
    "katcon.rvc_compliance": "Cumplimiento CVR",
    "katcon.duty_50_pct": "50% de arancel sobre contenido de acero",
    "katcon.exempt_via_correction": "Exención 232 asegurada vía corrección de HS",
    "katcon.no_232_exposure": "Sin exposición 232",

    "katcon.customs_232_title": "Exposición Sección 232 — Acero y Aluminio",
    "katcon.total_steel_content": "Contenido total de acero (MXN)",
    "katcon.verified_usmca": "Origen T-MEC verificado",
    "katcon.unverified_suppliers": "Proveedores sin verificar",
    "katcon.est_232_exposure": "Exposición 232 estimada (50%)",
    "katcon.th_origin": "Origen",
    "katcon.th_with_alisio": "Con Alisio",
    "katcon.tubes_issue_title": "El caso de los tubos",
    "katcon.tubes_issue_detail": "tiene origen verificado pero código HS incorrecto. Si se clasifica como 7306.40, la Sección 232 aplica al contenido de acero. Reclasificado como 8708.92, es una parte automotriz T-MEC — exento de 232. Esta sola reclasificación ahorra aproximadamente MXN $287,400 por embarque.",
    "katcon.tubes_corrected_suffix": "— Corrección aplicada.",
    "katcon.cbp_title": "Fortaleciendo el Argumento de Clasificación ante CBP",
    "katcon.cbp_intro": "Katcon ya ha intentado que CBP acepte la reclasificación de 7306.40 a 8708.92 y encontró resistencia. Es un problema de evidencia — CBP requiere documentación de uso principal, especificaciones de manufactura y trazabilidad de BOM. Alisio construye este paquete de evidencia automáticamente.",
    "katcon.evidence_bom": "Trazabilidad de BOM",
    "katcon.evidence_bom_detail": "Cada tubo tiene un número de parte específico vinculado a un ensamble de escape específico para un programa OEM identificado (ej., GM Silverado Euro 7 SCR). Esta es evidencia de uso principal — el tubo fue fabricado a especificación, no como producto genérico.",
    "katcon.evidence_bom_source": "Katcon SAP — datos de BOM",
    "katcon.evidence_mfg": "Especificaciones de Manufactura",
    "katcon.evidence_mfg_detail": "Los certificados de molino y OCs muestran que el tubo fue pedido con especificaciones para sistema de escape (aleación específica, espesor de pared, diámetro, radio de curvatura) inadecuado para aplicaciones generales de tubería.",
    "katcon.evidence_mfg_source": "Tell Tale — certificados de molino analizados + datos de OC del SAP",
    "katcon.evidence_enduse": "Declaración de Uso Final",
    "katcon.evidence_enduse_detail": "El certificado de cumplimiento firmado por Tubos y Perfiles incluye una declaración de uso previsto — fabricado específicamente para aplicaciones de escape automotriz.",
    "katcon.evidence_enduse_source": "Certificado de cumplimiento Opción 1",
    "katcon.evidence_precedent": "Investigación de Precedentes",
    "katcon.evidence_precedent_detail": "Referencia cruzada contra la base de datos CROSS de CBP (220,000+ resoluciones) para casos similares donde componentes de escape fueron clasificados exitosamente bajo 8708 a pesar de estar hechos de tubería de acero.",
    "katcon.evidence_precedent_source": "Motor de validación HS de Alisio",
    "katcon.evidence_assembled": "Paquete de evidencia preparado",
    "katcon.correction_propagation": "Cuando la reclasificación es aceptada — a través de una resolución vinculante, protesta o resolución negociada — la corrección se propaga a través de los 6 documentos aguas abajo automáticamente. Un cambio en el portal, seis documentos actualizados.",
    "katcon.mill_cert_monitoring": "Monitoreo de Cambios en Certificados de Molino",
    "katcon.mill_cert_intro": "Los certificados de molino no son estáticos. Los proveedores cambian fuentes de acero entre lotes, y cada cambio puede afectar el argumento de clasificación, la determinación de origen y la calificación T-MEC.",
    "katcon.monitor_source_change": "Cambio de Fuente del Proveedor",
    "katcon.monitor_source_detail": "Detecta cuando un certificado de molino para el mismo número de parte muestra un proveedor, aleación o ubicación de fundición diferente. Un cambio en el origen del acero puede invalidar la calificación T-MEC y reintroducir exposición 232.",
    "katcon.monitor_spec_drift": "Desviación de Especificación",
    "katcon.monitor_spec_detail": "Marca cuando el grado de material o especificaciones cambian en un certificado de molino (ej., SAE 304L a un grado diferente). Podría debilitar el argumento de 'fabricado a especificación de escape' para la disputa CBP.",
    "katcon.monitor_price_anomaly": "Anomalía de Precio",
    "katcon.monitor_price_detail": "Cuando el precio del acero cae significativamente por debajo de la referencia LME, puede indicar material transembarcado con declaraciones de origen falsas. Marca certificados que pueden no reflejar la fuente real.",
    "katcon.monitor_missing": "Faltante o Vencido",
    "katcon.monitor_missing_detail": "Marca cuando no se proporciona certificado de molino para un embarque, o cuando un certificado es más antiguo que la fecha de embarque. Presentar una declaración de origen T-MEC sin documentación actual crea exposición ante auditoría.",
    "katcon.immex_title": "Salud del Programa IMMEX",
    "katcon.immex_status": "Estado del Programa IMMEX",
    "katcon.immex_iva_cert": "Certificación IVA/IEPS",
    "katcon.immex_efos": "Filtrado EFOS de Proveedores",
    "katcon.immex_efos_value": "1 proveedor marcado — exposición",
    "katcon.immex_subsupplier": "Verificación de Sub-proveedores",
    "katcon.immex_subsupplier_value": "5/8 completamente verificados",
    "katcon.immex_annex24": "Cumplimiento Anexo 24",
    "katcon.immex_annex30": "Cumplimiento Anexo 30",
    "katcon.not_managed": "No administrado por Alisio",
    "katcon.risk_factors_title": "Factores de Riesgo",
    "katcon.risk_factor_efos": "Fundición Industrial Celaya en EFOS presunto — cualquier CFDI en documentos IMMEX crea riesgo de auditoría",
    "katcon.risk_factor_certs": "3 proveedores sin certificados de cumplimiento firmados",
    "katcon.risk_factor_hs": "Error(es) de clasificación HS en cadena de suministro activa",
    "katcon.article_25_title": "Recuperación Artículo 2.5",
    "katcon.article_25_detail": "Cuando los productos terminados se exportan bajo T-MEC, las exenciones de aranceles IMMEX sobre insumos no-TLC se recuperan a la tasa IGI de México — que aumentó 10-50% en enero de 2026. Cada insumo que Alisio verifica como originario T-MEC reduce esta exposición.",
    "katcon.scope_1": "Filtrado continuo EFOS 69-B",
    "katcon.scope_2": "Verificación de CFDI en tiempo real",
    "katcon.scope_3": "Validación de código HS y detección de errores de clasificación",
    "katcon.scope_4": "Análisis de certificados de molino para origen de fundición y vaciado",
    "katcon.scope_5": "Cálculo de CVR (Costo Neto + Valor de Transacción)",
    "katcon.scope_6": "Certificados de cumplimiento con firma digital",
    "katcon.scope_7": "Rastro de auditoría con marca de tiempo",
    "katcon.scope_8": "Paquetes de documentación listos para MV",
    "katcon.scope_not_1": "Control de inventario Anexo 24/30 (permanece en SAP)",
    "katcon.scope_not_2": "Presentación de pedimento en VUCEM (su agente aduanal lo maneja)",
    "katcon.scope_not_3": "Reemplazar a su agente aduanal",
    "katcon.scope_not_4": "Administración del programa IMMEX",
    "katcon.scope_not_5": "Inspección física de carga",
    "katcon.scope_footer": "Alisio verifica los datos antes de que lleguen a sus sistemas aduaneros. Su agente aduanal presenta con confianza porque los datos subyacentes han sido validados.",

    // Katcon Option 1
    "katcon.opt1_step1_detail": "Certificados de molino, facturas, declaraciones de origen enviados a verificacion@katcon.com. Los mismos documentos que ya producen, diferente dirección.",
    "katcon.opt1_step3_detail": "Certificado de cumplimiento pre-llenado con datos verificados. Problemas marcados para revisión de Katcon antes de enviar al proveedor.",
    "katcon.opt1_catch_efos_title": "EFOS — Fundición Celaya",
    "katcon.opt1_catch_efos_detail": "En la lista 69-B presunto del SAT. La Opción 1 detecta esto el día que el proveedor es listado — antes de que su próxima factura llegue a un pedimento.",
    "katcon.opt1_catch_cfdi_title": "CFDI Cancelado — Metalúrgica del Norte",
    "katcon.opt1_catch_cfdi_detail": "Factura MNO-2026-0847 ($234,600 MXN) cancelada después de que Katcon la registró. El monitoreo en tiempo real detecta la cancelación en horas.",
    "katcon.opt1_catch_tubes_title": "Tubos — Tubos y Perfiles MTY",
    "katcon.opt1_catch_tubes_resolved": "Resuelto — Reclasificado de 7306.40 a 8708.92. Paquete de evidencia preparado. Corrección propagada a 6 documentos.",
    "katcon.opt1_catch_tubes_unresolved": "Clasificado como 7306.40 (tubos de acero) en lugar de 8708.92 (partes de escape). Katcon ha intentado la reclasificación con CBP y encontró resistencia. La Opción 1 construye el paquete de evidencia que CBP requiere: trazabilidad de BOM mostrando uso principal, especificaciones de manufactura de certificados de molino, declaración de uso final en el certificado de cumplimiento, y precedentes de la base de datos CROSS.",
    "katcon.opt1_catch_mill_title": "Certificados de molino faltantes — 3 proveedores",
    "katcon.opt1_catch_mill_detail": "Sin documentación de fundición y vaciado, no se puede reclamar origen T-MEC. Al 50% de Sección 232, cada embarque sin verificar es exposición directa de costos.",
    "katcon.opt1_sap_note_title": "Integración SAP S/4HANA:",
    "katcon.opt1_sap_note_detail": "Arreglo de comunicación de solo lectura sobre maestro de proveedores y catálogos de compras. 1-2 días de configuración. Vea la pestaña de Integración SAP para detalles técnicos completos.",

    "katcon.opt2_key_message": "Esto es lo que mostramos en la demo del viernes. Es el producto correcto para proveedores que quieren el beneficio. Para verificación de cumplimiento, la Opción 1 es todo lo que Katcon necesita. La Opción 2 es el incentivo que motiva a algunos proveedores a ir más allá voluntariamente.",

    // Katcon SAP
    "katcon.sap_title": "Integración SAP S/4HANA Nube Pública",
    "katcon.sap_detail": "Solo lectura. Sin código personalizado. Se ajusta a su enfoque Fit to Standard.",
    "katcon.sap_case2_detail": "Alisio verifica a los sub-proveedores de Katcon usando datos que ya están en el SAP de Katcon. Los sub-proveedores no otorgan ningún acceso.",
    "katcon.sap_catalogs": "Catálogos de Negocio",
    "katcon.sap_catalogs_value": "Maestro de Proveedores, Compras, CP (lectura)",
    "katcon.sap_integration_value": "Arreglo de Comunicación",
    "katcon.sap_not_1": "Sin código ABAP personalizado",
    "katcon.sap_not_2": "Sin cambios a Roles de Negocio existentes",
    "katcon.sap_not_3": "Sin acceso de escritura a ningún catálogo",
    "katcon.sap_not_4": "Sin modificaciones a aplicaciones Fiori",
    "katcon.sap_not_5": "Sin acceso a entorno de desarrollo ABAP",
    "katcon.sap_case1_title": "Caso 1: Verificar el Cumplimiento Propio de Katcon",
    "katcon.sap_case1_detail": "Katcon verifica su propia emisión de CFDI, situación fiscal y documentación T-MEC para clientes OEM.",
    "katcon.sap_case1_1": "CFDIs emitidos por Katcon",
    "katcon.sap_arch_sap": "Katcon SAP",
    "katcon.sap_arch_sap_sub": "S/4HANA Nube Pública",
    "katcon.sap_arch_comm": "Arreglo de Comunicación",
    "katcon.sap_arch_portal": "/katcon-portal",
    "katcon.sap_arch_footer": "El trabajo pesado ocurre Alisio-SAT, no Alisio-SAP.",
    "katcon.sap_qa_fit": "Fit to Standard",

    // EDICOM
    "katcon.edicom_title": "Complemento con EDICOM",
    "katcon.edicom_detail": "EDICOM maneja el timbrado de CFDI (lado de emisión). Alisio maneja la verificación (lado de cumplimiento). Nunca competimos — siempre somos complementarios. Los datos de EDICOM pueden proporcionar la ruta existente de SAP para CFDIs entrantes.",
    "katcon.sap_edicom_note": "EDICOM puede proporcionar una ruta de datos existente para CFDIs entrantes vía integración SAP.",

    // Alerts
    "katcon.alert_resolved_title": "Resuelto — Error de Clasificación HS (Tubos y Perfiles MTY)",
    "katcon.alert_resolved_detail": "HS 7306.40 → 8708.92 — Exención Sección 232 asegurada, 6 documentos actualizados",

    // Correction workflow
    "katcon.correction_title": "Corrección de Código HS — Propagación en Vivo",
    "katcon.correction_detail": "Una corrección se propaga a través de toda la cadena de documentos. Como actualizar una invitación de calendario — cada documento vinculado recibe el cambio.",
    "katcon.correction_section": "Corrección",
    "katcon.correction_field": "Campo",
    "katcon.correction_current": "Actual (incorrecto)",
    "katcon.correction_current_value": "7306.40 — Tubos de Acero Soldados",
    "katcon.correction_corrected": "Corregido",
    "katcon.correction_corrected_value": "8708.92 — Partes de Sistema de Escape",
    "katcon.correction_applied_by": "Aplicado por",
    "katcon.correction_applied_by_value": "Alisio — Motor de Validación HS",
    "katcon.correction_timestamp": "Marca de Tiempo",
    "katcon.correction_downstream": "Documentos aguas abajo",
    "katcon.correction_downstream_count": "6 afectados",
    "katcon.confirm_correction": "Confirmar Corrección",
    "katcon.propagating": "Propagando...",
    "katcon.correction_applied": "Corrección Aplicada",
    "katcon.downstream_docs": "Documentos Aguas Abajo",
    "katcon.doc_invoice": "Factura Comercial",
    "katcon.doc_cove": "COVE (Comprobante de Valor Electrónico)",
    "katcon.doc_mv": "Manifestación de Valor (E2)",
    "katcon.doc_usmca": "Certificado de Origen T-MEC",
    "katcon.doc_232": "Determinación Sección 232",
    "katcon.doc_pedimento": "Pedimento",
    "katcon.doc_232_exempt": "232 EXENTO",
    "katcon.correction_summary": "1 corrección → 6 documentos actualizados → Calificación T-MEC confirmada → Exención Sección 232 asegurada",
    "katcon.propagation_time": "Tiempo de propagación:",
    "katcon.manual_time": "Proceso manual: est. 4-6 horas",

    // Tariff calculator
    "katcon.roi_title": "ROI de Cumplimiento — Aranceles Evitados",
    "katcon.roi_monthly_shipments": "Embarques mensuales verificados",
    "katcon.roi_duties_without": "Aranceles sin verificación",
    "katcon.roi_duties_with": "Aranceles con Alisio",
    "katcon.roi_monthly_savings": "Ahorro mensual",
    "katcon.roi_without": "Sin verificación",
    "katcon.roi_with": "Con Alisio",
    "katcon.roi_ytd": "Ahorro acumulado anual",
    "katcon.roi_annualized": "Anualizado",
    "katcon.roi_rate_without": "Tasa efectiva sin",
    "katcon.roi_rate_with": "Tasa efectiva con Alisio",
    "katcon.roi_disclaimer": "Calculado con datos representativos. Conecte su SAP para cálculos basados en volúmenes reales de compra.",
    "katcon.tariff_title": "Calculadora de Compensación Arancelaria",
    "katcon.tariff_help": "Vea el impacto financiero de cada paso de verificación por embarque.",
    "katcon.tariff_hs_code": "Código HS",
    "katcon.tariff_shipment_value": "Valor del Embarque (MXN)",
    "katcon.tariff_steel_content": "Contenido de Acero/Aluminio (MXN)",
    "katcon.tariff_calculate": "Calcular",
    "katcon.tariff_scenario": "Escenario",
    "katcon.tariff_igi_rate": "Tasa IGI",
    "katcon.tariff_igi_amount": "Monto IGI",
    "katcon.tariff_232_rate": "Tasa 232",
    "katcon.tariff_232_amount": "Monto 232",
    "katcon.tariff_total": "Aranceles Totales",
    "katcon.tariff_savings": "Ahorro",

    // Landed cost
    "katcon.landed_title": "Inteligencia de Costo de Internación",
    "katcon.landed_help": "Costo de internación real por producto — calculado con datos de compra de SAP + tratamiento arancelario verificado.",
    "katcon.landed_sap": "SAP S/4HANA",
    "katcon.landed_sap_sub": "Precio, flete, BOM",
    "katcon.landed_alisio": "Motor de Tasas Alisio",
    "katcon.landed_alisio_sub": "IGI, PROSEC, 232",
    "katcon.landed_output": "Costo de Internación",
    "katcon.landed_output_sub": "Por unidad, por embarque",
    "katcon.landed_price": "Precio/Unidad (MXN)",
    "katcon.landed_qty": "Cant.",
    "katcon.landed_freight": "Flete (MXN)",
    "katcon.landed_steel": "Contenido de Acero (MXN)",
    "katcon.landed_origin_verified": "Origen Verificado",
    "katcon.landed_prosec": "PROSEC",
    "katcon.landed_line_purchase": "Precio de Compra",
    "katcon.landed_line_freight": "Flete + Seguro",
    "katcon.landed_line_igi": "Arancel de Importación (IGI)",
    "katcon.landed_line_prosec": "Ajuste PROSEC",
    "katcon.landed_line_usmca": "Preferencial T-MEC",
    "katcon.landed_line_232": "Sección 232",
    "katcon.landed_line_iva": "IVA (16%)",
    "katcon.landed_iva_detail": "Acreditado con certificación IMMEX",
    "katcon.landed_line_dta": "DTA",
    "katcon.landed_dta_detail": "0.8% máximo",
    "katcon.landed_line_broker": "Honorarios de Agente Aduanal (est.)",
    "katcon.landed_line_total": "COSTO TOTAL DE INTERNACIÓN",

    // Email demo katcon
    "katcon.email_hs_issue": "Problema HS",
    "katcon.email_corrected": "Corregido",
    "katcon.email_hs_mismatch": "NO COINCIDE — debería ser 8708.92 (partes de escape)",
    "katcon.email_po_match": "Coincide — dentro de 0.2% del valor de OC",
    "katcon.cert_hs_warn": "Código HS 7306.40 debería ser 8708.92 — se recomienda corrección antes de emitir certificado.",
    "katcon.cert_hs_corrected": "Todos los campos verificados. Código HS corregido a 8708.92 — certificado listo para emisión inmediata.",
    "katcon.cert_warn_review": "campo(s) marcado(s) para revisión de Katcon antes de enviar al proveedor.",

    // =========================================================================
    // NEXTEER SPANISH
    // =========================================================================
    "nexteer.demo_banner": "Modo Demo — Mostrando datos representativos de la cadena de suministro de Nexteer en QRO. Conecte su ERP para datos en vivo.",
    "nexteer.icts_deadline": "ICTS MY 2027",
    "nexteer.tab_icts": "Cumplimiento ICTS",
    "nexteer.tab_usmca": "T-MEC y CVR",
    "nexteer.tab_relocation": "Rastreo de Reubicación",
    "nexteer.tab_erp": "Integración ERP",

    "nexteer.rfc_help": "Ingrese RFCs de proveedores para ver resultados de filtrado EFOS 69-B. Pruebe: HSC780901FFF (marcado) o FPQ870315AAA (limpio). SZS190820CCC está limpio pero alto riesgo (ICTS).",
    "nexteer.stat_icts_exposure": "Exposición ICTS",
    "nexteer.stat_icts_sub": "componente(s)",
    "nexteer.stat_rvc_margin": "Margen CVR",
    "nexteer.stat_rvc_margin_sub": "pts por encima del umbral",
    "nexteer.stat_relocations": "Reubicaciones Activas",
    "nexteer.stat_relocations_sub": "en transición",
    "nexteer.stat_mv_ready": "Listo para MV",
    "nexteer.overview_title": "Resumen de Cumplimiento de Proveedores",
    "nexteer.th_icts": "ICTS",
    "nexteer.th_relocation": "Reubicación",
    "nexteer.th_efos": "EFOS",
    "nexteer.status_established": "Establecido",
    "nexteer.remediation_history": "Historial de Remediación",
    "nexteer.remediation_resolved": "Problema ICTS MCU resuelto vía ruta {path}",

    // ICTS
    "nexteer.icts_dashboard": "Panel de Estado de Regla ICTS",
    "nexteer.icts_software_deadline": "Plazo Software MY 2027",
    "nexteer.icts_hardware_deadline": "Plazo Hardware MY 2030",
    "nexteer.icts_declaration": "Declaración de Conformidad",
    "nexteer.icts_not_filed": "Aún no presentada",
    "nexteer.icts_bis_advisory": "Opinión Consultiva BIS",
    "nexteer.icts_not_requested": "No solicitada",
    "nexteer.icts_matrix_title": "Matriz de Clasificación de Componentes",
    "nexteer.th_covered_software": "¿Contiene Software Cubierto?",
    "nexteer.th_vcs_hardware": "¿Contiene Hardware VCS?",
    "nexteer.th_icts_risk": "Nivel de Riesgo ICTS",
    "nexteer.th_notes": "Notas",
    "nexteer.icts_eps": "Dirección Eléctrica Asistida (EPS)",
    "nexteer.icts_eps_note": "Producto principal más expuesto",
    "nexteer.icts_sbw": "Dirección por Cable",
    "nexteer.icts_sbw_note": "Crítico para seguridad, mayor escrutinio",
    "nexteer.icts_columns": "Columnas de Dirección",
    "nexteer.icts_columns_note": "Mayormente mecánicas salvo inclinación/telescopio eléctrico",
    "nexteer.icts_shafts": "Ejes Intermedios",
    "nexteer.icts_shafts_note": "Puramente mecánicos",
    "nexteer.icts_driveline": "Semiejes/Tren Motriz",
    "nexteer.icts_bbw": "Frenado por Cable",
    "nexteer.icts_bbw_note": "Nueva línea de producto, misma exposición ICTS",
    "nexteer.icts_audit_title": "Auditoría ICTS de Cadena de Suministro",
    "nexteer.th_country_design": "País de Diseño",
    "nexteer.th_country_mfg": "País de Manufactura",
    "nexteer.th_avic_nexus": "Nexo AVIC",
    "nexteer.th_mitigation": "Estado de Mitigación",
    "nexteer.avic_yes": "SÍ",
    "nexteer.avic_no": "NO",
    "nexteer.mitigation_accelerated": "Reubicación acelerada — 100% abastecimiento México",
    "nexteer.mitigation_unmitigated": "SIN MITIGAR — acción requerida",
    "nexteer.icts_does_1": "Monitorear reglamentación y opiniones consultivas de BIS",
    "nexteer.icts_does_2": "Rastrear cadena de suministro a nivel componente para productos cubiertos por ICTS",
    "nexteer.icts_does_3": "Mapear propiedad de proveedores sub-tier a designaciones de adversarios extranjeros",
    "nexteer.icts_does_4": "Alertar cuando cambie la propiedad o ubicación de manufactura del proveedor",
    "nexteer.icts_does_5": "Mantener rastro de auditoría ICTS para soporte de Declaración de Conformidad",
    "nexteer.icts_not_1": "Proporcionar opiniones legales sobre si las transacciones están prohibidas",
    "nexteer.icts_not_2": "Presentar solicitudes de opinión consultiva a BIS",
    "nexteer.icts_not_3": "Hacer determinaciones legales sobre clasificación de 'software cubierto'",
    "nexteer.icts_not_4": "Reemplazar asesoría legal en estrategia de cumplimiento ICTS",
    "nexteer.icts_footer": "Alisio proporciona los datos y el monitoreo — no la determinación legal. Para interpretación legal, consulte a su abogado de comercio exterior.",

    // USMCA & RVC
    "nexteer.rvc_dashboard": "Contenido de Valor Regional por Línea de Producto",
    "nexteer.core_part": "parte fundamental",
    "nexteer.principal_part": "parte principal",
    "nexteer.complementary_part": "parte complementaria",
    "nexteer.margin": "margen",
    "nexteer.core_part_title": "Clasificación de Parte Fundamental T-MEC",
    "nexteer.core_part_desc": "Bajo T-MEC, los sistemas de dirección son una de siete 'partes fundamentales' que deben cumplir independientemente el umbral de 75% de CVR. Este es el umbral más alto del acuerdo.",
    "nexteer.th_category": "Categoría",
    "nexteer.th_threshold": "Umbral",
    "nexteer.th_nexteer_products": "Productos Nexteer",
    "nexteer.cat_core": "Parte Fundamental (S4-B.3)",
    "nexteer.cat_principal": "Parte Principal",
    "nexteer.cat_complementary": "Parte Complementaria",
    "nexteer.products_core": "EPS, Dirección por Cable, Columnas de Dirección",
    "nexteer.products_principal": "Semiejes, Juntas de tren motriz",
    "nexteer.products_complementary": "Ejes intermedios, Juntas universales",
    "nexteer.rvc_calc_title": "Calculadora de CVR (Método Costo Neto)",
    "nexteer.rvc_calc_desc": "CVR = ((CN - VNM) / CN) x 100 donde CN = Costo Neto y VNM = Valor de Materiales No Originarios. Pre-poblado con datos de ensamble EPS.",
    "nexteer.net_cost_label": "Costo Neto (MXN)",
    "nexteer.vnm_label": "Materiales No Originarios (MXN)",
    "nexteer.calculate_rvc": "Calcular CVR",
    "nexteer.calculated_rvc": "CVR Calculado (Método Costo Neto)",
    "nexteer.steel_origin_title": "Verificación de Origen de Acero/Aluminio (Artículo 4.6)",
    "nexteer.steel_origin_desc": "70% del acero debe ser fundido y vaciado en un país T-MEC. Para las barras de forja de Nexteer, esto significa rastrear la palanquilla hasta el horno de fundición.",
    "nexteer.th_steel_grade": "Grado de Acero",
    "nexteer.th_mill_source": "Fuente del Molino",
    "nexteer.th_usmca_origin": "Origen T-MEC",
    "nexteer.th_melt_pour": "Fundido y Vaciado",
    "nexteer.joint_review_title": "Monitor de Revisión Conjunta",
    "nexteer.joint_review_event_1": "USITC lanza investigación sobre reglas de origen automotriz T-MEC",
    "nexteer.joint_review_event_2": "Testimonio EPI señala empresas afiliadas a China",
    "nexteer.joint_review_event_3": "Audiencias públicas esperadas",
    "nexteer.joint_review_event_4": "Revisión Conjunta formal comienza",
    "nexteer.joint_review_proposals": "Propuestas clave en consideración:",
    "nexteer.proposal_1": "Aumentos de umbral (partes fundamentales de 75% a 80%)",
    "nexteer.proposal_2": "Escrutinio de contenido chino y requisitos de rastreo",
    "nexteer.proposal_3": "Cambios a la provisión de acumulación",
    "nexteer.impact_title": "Modelado de Impacto: Aumento de Umbral a 80%",
    "nexteer.impact_desc": "Si el umbral aumenta de 75% a 80%, EPS (78.2%) y Dirección por Cable (76.1%) FALLARÍAN. Requiere mejora de origen para componentes de Suzhou.",
    "nexteer.rollup_title": "Provisión de Acumulación (Disputa de Panel 2023)",
    "nexteer.rollup_p1": "La disputa del panel 2023: México/Canadá argumentaron que las partes fundamentales que cumplen 75% obtienen su valor completo contado como originario a nivel vehículo. EE.UU. no estuvo de acuerdo. El panel falló a favor de México/Canadá.",
    "nexteer.rollup_p2": "Si se revierte en la revisión 2026, los OEMs necesitarían rastrear materiales no originarios a través de los productos de Nexteer hasta el nivel vehículo. Esto aumentaría significativamente los requisitos de documentación y podría impactar la posición comercial de Nexteer con clientes OEM.",
    "nexteer.s232_title": "Sección 232 — Contenido de Acero Aleado",
    "nexteer.s232_desc": "Nexteer usa acero aleado para forjas. 232 aplica al contenido de acero, pero forja + maquinado + ensamble agrega suficiente valor para que los componentes de dirección terminados típicamente califiquen bajo T-MEC y eviten 232.",
    "nexteer.s232_monthly": "Contenido mensual de acero (MXN)",
    "nexteer.s232_verified": "Origen T-MEC verificado",
    "nexteer.s232_exposure": "Exposición 232 (sin verificar)",

    "nexteer.steering_definition": "El sistema de dirección es definido por T-MEC como una de siete partes fundamentales: motor, transmisión, carrocería y chasis, eje, sistema de dirección, sistema de suspensión y batería avanzada. Los componentes incluyen columnas de dirección, cremalleras/cajas de dirección y unidades de control. Cada uno debe cumplir independientemente el umbral de 75% de CVR.",
    "nexteer.super_core": "T-MEC proporciona flexibilidad en CÓMO se calcula el CVR de dirección. Nexteer puede calcular a nivel del sistema de dirección completo, a nivel de componente enumerado, o a través de todas las partes fundamentales juntas. La calculadora de CVR de Alisio modela todos los métodos y muestra cuál da el mejor resultado.",
    "nexteer.rollup_status": "El panel de disputa de 2023 dictaminó que las partes fundamentales que cumplen 75% de CVR independientemente pueden contar como 100% originarias a nivel vehículo ('acumulación'). EE.UU. impugnó esto. Sigue siendo un tema clave en la Revisión Conjunta 2026. Si se revierte, el contenido no originario dentro de la dirección de Nexteer (MCUs de origen chino, acero no T-MEC) se rastrearía hasta el nivel vehículo.",
    "nexteer.steel_2027": "A partir de julio 2027 (7 años después de la entrada en vigor de T-MEC), el acero solo podrá calificar como originario si TODOS los procesos de manufactura del acero ocurren en países T-MEC. Los componentes de acero forjado de Nexteer (barras cremallera SAE 4140/4340, ejes piñón) necesitan acero completamente fundido y vaciado en países T-MEC.",
    "nexteer.halfshafts_note": "Los productos de tren motriz de Nexteer (semiejes, juntas de flecha cardán) se clasifican bajo 8708.50 — Tabla B (partes principales) al 70%, NO Tabla A (partes fundamentales) al 75%. La presión de cumplimiento es menor en tren motriz que en dirección.",

    "nexteer.ecu_dispute_alert": "Clasificación de ECU en disputa — si se clasifica bajo 8537 (Cap.85) en lugar de 8708.94 (Cap.87), este componente queda fuera del cálculo de CVR de parte fundamental del sistema de dirección. Esto cambia las matemáticas para el umbral de 75%.",

    // Relocation
    "nexteer.relocation_title": "Tablero de Estado de Reubicación",
    "nexteer.relocation_suppliers": "13 proveedores",
    "nexteer.stage_planning": "Planeación",
    "nexteer.stage_establishing": "Establecimiento",
    "nexteer.stage_qualifying": "Calificación",
    "nexteer.stage_transitioning": "Transición",
    "nexteer.stage_complete": "Completa",
    "nexteer.th_from": "Desde",
    "nexteer.th_to": "Hacia",
    "nexteer.th_stage": "Etapa",
    "nexteer.th_mexico_pct": "% México",
    "nexteer.th_usmca_impact": "Impacto T-MEC",
    "nexteer.th_icts_impact": "Impacto ICTS",
    "nexteer.mixed_origin_title": "Riesgo de Origen Mixto Durante la Transición",
    "nexteer.mixed_origin_desc": "Durante las transiciones de reubicación, los proveedores embarcan desde instalaciones tanto chinas como mexicanas. Esto crea un desafío de documentación de doble fuente: la documentación de cumplimiento debe rastrear el origen por embarque, no por proveedor.",
    "nexteer.mixed_origin_warning": "Cuando un proveedor embarca material de origen chino durante la transición, la documentación de cumplimiento debe rastrear el origen por embarque — no por proveedor. Cada escaneo en el muelle de recepción debe verificar la planta real de manufactura para calcular correctamente el CVR para ese lote de producción.",
    "nexteer.mixed_origin_clear": "No hay proveedores actualmente en etapa de transición de doble fuente. El riesgo de documentación de origen mixto es mínimo.",
    "nexteer.aggregate_rvc_title": "Impacto Agregado de CVR — Ensamble EPS",
    "nexteer.current_rvc": "CVR Actual (EPS)",
    "nexteer.projected_rvc": "CVR Proyectado (13 reubicaciones completas)",
    "nexteer.remediated": "remediado",

    // Suppliers
    "nexteer.icts_risk": "Riesgo ICTS",
    "nexteer.relocation_progress": "Progreso de Reubicación",
    "nexteer.rvc_contribution": "Contribución CVR",
    "nexteer.resolve_icts": "Resolver Problema ICTS",

    // Alerts
    "nexteer.alert_resolved_title": "Resuelto — Problema ICTS MCU (Suzhou Steering Components de México)",
    "nexteer.alert_resolved_detail": "Remediado vía ruta {path} — dependencia de subsidiaria AVIC eliminada, cumplimiento GM MY 2027 restaurado",

    // Option 1 nexteer
    "nexteer.opt1_step1_detail": "Certificados de molino, facturas, declaraciones de origen enviados a verificacion@nexteer.com. Los mismos documentos que ya producen, diferente dirección.",
    "nexteer.opt1_step2_detail": "Valores de transacción cruzados con SAT. RFC filtrado contra 69-B. Códigos HS validados. Precio del acero verificado contra LME. Orígenes de componentes ICTS marcados.",
    "nexteer.opt1_step3_detail": "Certificado de cumplimiento pre-llenado con datos verificados. Problemas marcados para revisión de Nexteer antes de enviar al proveedor.",
    "nexteer.opt1_catch_efos_title": "EFOS — Hules y Sellos del Centro",
    "nexteer.opt1_catch_efos_detail": "Hules y Sellos del Centro en lista 69-B presunto. La Opción 1 detecta esto el día que el proveedor es listado — antes de que su próxima factura llegue a un pedimento.",
    "nexteer.opt1_catch_origin_title": "Origen — Jiangsu Auto Parts MTY",
    "nexteer.opt1_catch_origin_detail": "Certificado de molino de Jiangsu muestra acero chino — no cuenta para T-MEC Artículo 4.6. Al 50% de Sección 232, cada embarque sin verificar es exposición directa de costos.",
    "nexteer.opt1_catch_icts_title": "ICTS — Suzhou Steering Components",
    "nexteer.opt1_catch_icts_unresolved": "Componente electrónico sub-tier en ECU de Suzhou tiene origen de manufactura AVIC no revelado. Cae bajo la prohibición ICTS de vehículos conectados para plataformas GM MY 2027.",
    "nexteer.opt1_catch_icts_resolved": "Resuelto — Dependencia de subsidiaria AVIC eliminada. Abastecimiento de MCU redirigido a proveedor conforme. Paquete de documentación ICTS preparado.",
    "nexteer.opt1_see_remediation": "Ver opciones de remediación",
    "nexteer.opt1_catch_value_title": "Valor — Precios de proveedor reubicado",
    "nexteer.opt1_catch_value_detail": "Proveedor reubicado facturando a precios de nivel China — sugiere re-facturación en lugar de manufactura real en México. Anomalía de valor de transacción marcada para revisión.",
    "nexteer.opt1_liability_icts": "Para cumplimiento ICTS, Nexteer debe presentar Declaraciones de Conformidad anuales. El certificado de cumplimiento de la Opción 1 proporciona documentación por proveedor, por componente que respalda esta declaración anual.",
    "nexteer.opt1_erp_note_title": "Integración ERP:",
    "nexteer.opt1_erp_note_detail": "Conexión API de solo lectura a datos maestros de proveedores y compras. 1-2 días de configuración. Vea la pestaña de Integración ERP para detalles técnicos completos.",

    "nexteer.opt2_key_message": "Para verificación de cumplimiento, la Opción 1 es todo lo que Nexteer necesita. La Opción 2 es el incentivo que motiva a algunos proveedores a ir más allá voluntariamente. Los proveedores que quieren el beneficio se inscriben — no se requiere mandato.",

    // ERP nexteer
    "nexteer.erp_title": "Integración ERP",
    "nexteer.erp_detail": "Solo lectura. Ligero. Funciona con SAP, Oracle o cualquier ERP moderno.",
    "nexteer.erp_case2_detail": "Alisio verifica a los sub-proveedores de Nexteer usando datos que ya están en el ERP de Nexteer. Los sub-proveedores no otorgan ningún acceso.",
    "nexteer.erp_integration_value": "Integración API (REST u OData)",
    "nexteer.erp_case1_title": "Caso 1: Verificar el Cumplimiento Propio de Nexteer",
    "nexteer.erp_case1_detail": "Nexteer verifica su propia emisión de CFDI, situación fiscal y documentación T-MEC para clientes OEM.",
    "nexteer.erp_case1_1": "CFDIs emitidos por Nexteer",
    "nexteer.erp_arch_erp": "ERP Nexteer",
    "nexteer.erp_arch_erp_sub": "SAP, Oracle o equivalente",
    "nexteer.erp_arch_rest": "Integración REST / OData",
    "nexteer.erp_arch_portal": "/nexteer-portal",
    "nexteer.erp_qa_platform": "Plataforma ERP",
    "nexteer.erp_qa_platform_answer": "Por definir (SAP, Oracle o equivalente)",
    "nexteer.erp_qa_setup_case2": "Configuración (Caso 2)",
    "nexteer.erp_qa_setup_both": "Configuración (Caso 1+2)",

    // Passport
    "nexteer.passport_title": "Pasaporte Digital de Producto",
    "nexteer.passport_desc": "Instantánea de cumplimiento por unidad para un ensamble EPS individual. Combina verificación de proveedores, rastreo de origen y estatus regulatorio en un solo registro auditable.",
    "nexteer.passport_product_title": "Pasaporte de Producto — Unidad EPS KAT-EPS-2026-0112",
    "nexteer.passport_product": "Producto",
    "nexteer.passport_product_value": "Unidad de Dirección Eléctrica Asistida",
    "nexteer.passport_assembly": "Ensamble",
    "nexteer.passport_assembly_value": "Nexteer QRO Planta 1",
    "nexteer.passport_components": "Componentes",
    "nexteer.passport_tracked": "rastreados",
    "nexteer.passport_verification": "Capa de Verificación Alisio",
    "nexteer.passport_rvc": "Contenido de Valor Regional (CVR)",
    "nexteer.passport_rvc_above": "Por encima del umbral T-MEC de 75%",
    "nexteer.passport_rvc_below": "Por debajo del umbral T-MEC de 75%",
    "nexteer.passport_icts_status": "Estado ICTS",
    "nexteer.passport_icts_compliant": "Conforme",
    "nexteer.passport_icts_at_risk": "En riesgo — dependencia AVIC",
    "nexteer.passport_steel_origin": "Origen del Acero",
    "nexteer.passport_compliance_score": "Puntuación de Cumplimiento",
    "nexteer.passport_excellent": "Excelente",
    "nexteer.passport_good": "Bueno — problemas marcados",
    "nexteer.passport_needs_attention": "Requiere atención",

    // Remediation
    "nexteer.remediation_title": "Remediación ICTS — Suzhou Steering Components",
    "nexteer.remediation_desc": "El subensamble ECU contiene microcontrolador fabricado por subsidiaria AVIC. Tres rutas de remediación disponibles.",
    "nexteer.remediation_current_state": "Estado Actual",
    "nexteer.remediation_supplier": "Proveedor:",
    "nexteer.remediation_component": "Componente:",
    "nexteer.remediation_component_value": "Sensor de torque + subensamble ECU para EPS",
    "nexteer.remediation_issue": "Problema:",
    "nexteer.remediation_issue_value": "MCU fabricado por subsidiaria AVIC — transacción prohibida ICTS para vehículos conectados MY 2027",
    "nexteer.remediation_sourcing": "Abastecimiento actual:",
    "nexteer.remediation_sourcing_value": "60% México / 40% China",
    "nexteer.remediation_rvc_impact": "Impacto CVR actual:",
    "nexteer.remediation_rvc_value": "EPS al 78.2% (3.2 pts por encima del umbral)",
    "nexteer.remediation_oem": "Exposición OEM:",
    "nexteer.remediation_oem_value": "Vehículos conectados GM, plazo MY 2027",
    "nexteer.remediation_paths": "Rutas de Remediación",
    "nexteer.path_accelerate": "Acelerar Reubicación",
    "nexteer.path_recommended": "RECOMENDADO",
    "nexteer.path_accelerate_desc": "Mover a 100% abastecimiento México. Elimina tanto riesgo ICTS como CVR simultáneamente.",
    "nexteer.path_timeline": "Plazo:",
    "nexteer.path_accelerate_timeline": "6 semanas",
    "nexteer.path_icts": "ICTS:",
    "nexteer.path_accelerate_icts": "Riesgo ICTS eliminado — sin componentes fabricados en China",
    "nexteer.path_rvc": "CVR:",
    "nexteer.path_accelerate_rvc": "CVR EPS aumenta de 78.2% a 83.4% (+5.2 pts)",
    "nexteer.path_cost": "Costo:",
    "nexteer.path_accelerate_cost": "Costos de transición: ~$120K USD (herramental, calificación, logística)",
    "nexteer.path_select": "Seleccionar Esta Ruta",
    "nexteer.path_mcu": "Sustituir MCU",
    "nexteer.path_mcu_desc": "Reemplazar MCU fabricado por AVIC con alternativa (Texas Instruments, NXP). Requiere recalificación de ECU.",
    "nexteer.path_mcu_timeline": "8-12 semanas",
    "nexteer.path_mcu_icts": "Riesgo ICTS eliminado para ECU específicamente",
    "nexteer.path_mcu_rvc": "Cambio mínimo — costo del MCU es porción pequeña del valor del ECU",
    "nexteer.path_mcu_cost": "Ingeniería: ~$200K USD (rediseño, calificación, validación)",
    "nexteer.path_bis": "Autorización Específica BIS",
    "nexteer.path_bis_desc": "Solicitar autorización de BIS para uso continuado durante periodo de transición.",
    "nexteer.path_bis_timeline": "60+ días para opinión consultiva, sin garantía",
    "nexteer.path_bis_icts": "Alivio temporal si se otorga — no es solución permanente",
    "nexteer.path_bis_rvc": "Sin cambio",
    "nexteer.path_bis_cost": "Honorarios legales: ~$50-80K USD",
    "nexteer.path_selected": "Seleccionado",
    "nexteer.cascade_1": "Operaciones Suzhou México → 100% capacidad",
    "nexteer.cascade_2": "Abastecimiento China → terminado",
    "nexteer.cascade_3": "Auditoría ICTS → MCU removido de cadena de suministro",
    "nexteer.cascade_4": "Recálculo CVR → EPS 83.4% (+5.2 pts)",

    // Regulatory scenario toggle
    "nexteer.scenario_current": "Reglas Actuales (2026)",
    "nexteer.scenario_jr2026": "RC 2026 — Propuesto",
    "nexteer.scenario_2027": "2027 Acero + ICTS",
    "nexteer.scenario_jr2026_desc": "Fundamental 75%→80%. Acumulación revocada — contenido no originario se rastrea hasta el vehículo. Declaración de afiliación china (>25% propiedad).",
    "nexteer.scenario_jr2026_basis": "Investigación USITC 332-596, testimonio EPI ante el Congreso (Mar 2026), apelación FTA-MX-USA-2022-8708-01",
    "nexteer.scenario_2027_desc": "Todos los cambios RC 2026 + acero TODOS los procesos en T-MEC (endurecimiento Art. 4.6 Jul 2027) + prohibición hardware ICTS MY 2030 vigente.",
    "nexteer.scenario_2027_basis": "T-MEC Artículo 4.6 (transición de 7 años), Regla Final ICTS §7.100(b) disposiciones de hardware",
    "nexteer.scenario_rollup_overturned_title": "Provisión de acumulación revocada.",
    "nexteer.scenario_rollup_overturned_detail": "El contenido no originario dentro de los productos de dirección de Nexteer ahora se rastrea hasta el cálculo de CVR a nivel vehículo. Los OEMs (ej., GM) deben contabilizar el material no originario de Nexteer en su propio umbral de 75% a nivel vehículo. Esto cambia significativamente la posición comercial de Nexteer.",
    "nexteer.scenario_rollup_traces": "Acumulación revocada: {pct}% de contenido no originario se rastrea al nivel vehículo",
    "nexteer.scenario_affiliation_title": "Declaración de afiliación china activada.",
    "nexteer.scenario_affiliation_detail": "El beneficiario final de Nexteer (AVIC/China Aviation Industry Corporation) posee >{threshold}% de propiedad. Bajo las reglas propuestas, los proveedores Tier 1 con propiedad de nación adversaria extranjera por encima de este umbral deben declarar esto a los clientes OEM y CBP. Esto no cambia las matemáticas del CVR — crea una obligación adicional de reporte y puede afectar las decisiones de aprovisionamiento de los OEM.",
    "nexteer.scenario_steel_title": "Endurecimiento de origen del acero (Julio 2027).",
    "nexteer.scenario_steel_detail": "TODOS los procesos de manufactura del acero deben ocurrir en países T-MEC — no solo fundición y vaciado. Los componentes de acero forjado (barras cremallera SAE 4140/4340, ejes piñón) necesitan toda la cadena de manufactura verificada. Cualquier proveedor que use tochos o planchones importados pierde la calificación de origen T-MEC aunque la fundición haya ocurrido en la región.",
    "nexteer.scenario_icts_hw_title": "Prohibición de hardware ICTS (MY 2030).",
    "nexteer.scenario_icts_hw_detail": "Los componentes de hardware físico (sensores, actuadores, ECUs) de fabricantes de naciones adversarias extranjeras están prohibidos en vehículos conectados. Se extiende más allá del software a todo el hardware VCS. Todos los ensambles EPS y Dirección por Cable de Nexteer deben tener cadenas de suministro de hardware completamente conformes.",
    "nexteer.scenario_accelerated": "Remediación acelerada aplicada.",
    "nexteer.scenario_accelerated_detail": "Contenido de Suzhou trasladado a 100% abastecimiento México. EPS mejoró de 78.2% a 83.4%, Dirección por Cable de 76.1% a 81.8%.",

    // Email demo nexteer
    "nexteer.email_icts": "ICTS",
    "nexteer.email_origin": "Origen",
    "nexteer.email_seed": "semilla",
    "nexteer.cert_fail_steel": "Origen del acero: No originario T-MEC. Fundido y vaciado en China. Aplica exposición Sección 232.",
    "nexteer.cert_warn_icts": "MCU proveniente de subsidiaria AVIC — se requiere revisión ICTS de vehículos conectados antes de emitir certificado.",
    "nexteer.cert_warn_review": "campo(s) marcado(s) para revisión de Nexteer antes de enviar al proveedor.",
    "nexteer.cert_fail_msg": "campo(s) no pasaron la verificación. El certificado no puede emitirse hasta que se resuelva el cumplimiento de origen.",
    "nexteer.manual_review": "Revisión manual: est. 45-90 minutos por correo",
    // ── BUG-100 — Spanish entries for keys that resolved to their English
    // fallback on the MX demo. The call sites were already correct; only the
    // 'es' row was absent, so t(key,'English') silently returned English.

    'nexteer.steel_2027_title': 'Endurecimiento del Origen del Acero 2027',
    'nexteer.super_core_title': 'Disposición Super-Core',
},
};
